/**
 * Iter 195 · WhatsApp 2-way chat inbox.
 *
 * Left pane = thread list, right pane = active thread with message
 * composer. Session-message send is gated on the 24h Meta window.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  MessageCircle, Search, Send, Clock, Check, CheckCheck, User as UserIcon,
  Loader2, RefreshCw, ShieldAlert, UserPlus, AtSign, UserCheck, Flame, Trash2,
  Paperclip, FileText, Image as ImageIcon, Film, Music, Star, X, Upload, Wrench,
} from "lucide-react";
import { fmtRelative } from "@/lib/format";

/* Feb 2026 · Interakt Window Hint — compact helper that renders the
   time left until the 24h Meta session window shuts. We render this
   inside each thread chip + the active-thread badge so reps can tell
   whether a reply is safe or if a template is required next. */
function _fmtWindowRemaining(expiresIso, { compact = false } = {}) {
  if (!expiresIso) return compact ? "—" : "awaiting reply";
  const t = new Date(expiresIso).getTime();
  if (isNaN(t)) return compact ? "—" : "awaiting reply";
  const diffMs = t - Date.now();
  if (diffMs <= 0) return compact ? "expired" : "expired · needs template";
  const mins = Math.floor(diffMs / 60000);
  const hrs = Math.floor(mins / 60);
  const remM = mins % 60;
  if (hrs >= 1) return compact ? `${hrs}h${remM ? `${remM}m` : ""}` : `in ${hrs}h ${remM}m`;
  return compact ? `${mins}m` : `in ${mins}m`;
}

export default function WaInbox({ embedded = false, compact = false } = {}) {
  const { activeWorkspaceId: wsId, user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [threads, setThreads] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeRef, setActiveRef] = useState(params.get("ref") || null);
  const [filterAssign, setFilterAssign] = useState("all");
  // R64.2 · Read/Unread pill — quick toggle to focus on new messages.
  const [filterRead, setFilterRead] = useState("all"); // all | unread | read
  // R138ca · Feb 2026 — Auto-vs-manual assignment provenance filter.
  // "all" = show every thread, "auto" = only first-touch auto-assigns,
  // "manual" = only manually assigned (or legacy assignments without a
  // provenance stamp, which we treat as manual).
  const [filterSource, setFilterSource] = useState("all"); // all | auto | manual
  // R51 · Optional specific assignee picker (overrides filterAssign when set)
  const [assigneeId, setAssigneeId] = useState("");
  const [members, setMembers] = useState([]);
  // R51 · Track message ids across polls so we can toast on NEW inbounds.
  // Uses a ref so we don't retrigger the polling effect on every diff.
  const seenMessageIdsRef = useRef(null);
  const firstLoadRef = useRef(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/workspaces/${wsId}/wa/threads`,
                                        { params: { limit: 100 } });
      const rows = r.data.rows || [];
      // Seed the fingerprint so silentReloadThreads doesn't toast the
      // entire backlog after the initial load.
      seenMessageIdsRef.current = new Set(
        rows.filter(t => t.last_direction === "in" && t.last_at)
             .map(t => `${t.contact_ref}|${t.last_at}`)
      );
      firstLoadRef.current = false;
      setThreads(rows);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load threads")); }
    finally { setLoading(false); }
  };
  // Iter Feb 2026 R36 · Silent thread-list poller (no spinner / no toast on
  // failure). Runs every 10 seconds while the tab is visible so unread
  // counts + last-message previews for OTHER conversations stay fresh
  // as new customer replies land. Skipped while the tab is hidden so we
  // don't burn quota in the background.
  const silentReloadThreads = async () => {
    try {
      const r = await api.get(`/workspaces/${wsId}/wa/threads`,
                                        { params: { limit: 100 } });
      const rows = r.data.rows || [];
      // R51 · Detect brand-new inbounds and surface a floating toast per
      // new sender. Uses last-inbound-timestamp + last-text as the fingerprint
      // (wa_messages ids aren't in this payload). Skipped on the very
      // first load so we don't spam toasts for pre-existing threads.
      if (seenMessageIdsRef.current && !firstLoadRef.current) {
        const before = seenMessageIdsRef.current;
        for (const t of rows) {
          if (t.last_direction !== "in" || !t.last_at) continue;
          const key = `${t.contact_ref}|${t.last_at}`;
          if (before.has(key)) continue;
          const preview = (t.last_text || "").slice(0, 120)
                            || "(non-text message)";
          const who = t.name || t.phone || "New contact";
          toast(`💬 ${who}`, {
            description: preview,
            duration: 5000,
            action: {
              label: "Open",
              onClick: () => {
                setActiveRef(t.contact_ref);
                setParams({ ref: t.contact_ref });
              },
            },
          });
        }
        window.dispatchEvent(new CustomEvent("acq:wa-unread-refresh"));
      }
      seenMessageIdsRef.current = new Set(
        rows.filter(t => t.last_direction === "in" && t.last_at)
             .map(t => `${t.contact_ref}|${t.last_at}`)
      );
      firstLoadRef.current = false;
      setThreads(rows);
    } catch { /* swallow — the visible list retains its last-known state */ }
  };
  useEffect(() => { if (wsId) load(); }, [wsId]);
  useEffect(() => {
    if (!wsId) return undefined;
    const iv = setInterval(() => {
      if (document.visibilityState === "visible") silentReloadThreads();
    }, 10_000);
    // Also refresh immediately when the tab regains focus after being away.
    const onVis = () => { if (document.visibilityState === "visible") silentReloadThreads(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", onVis); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsId]);

  // Load workspace members for assign / mention pickers
  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/members`)
      .then((r) => setMembers((r.data || []).filter(m => m.status !== "removed")))
      .catch(() => setMembers([]));
  }, [wsId]);

  // R51 · Optimistically zero-out the per-thread unread badge on the
  // sidebar the moment ANY code path fires `acq:wa-thread-read` — the
  // active-thread mark-read broadcast, the mini inbox mark-read, or a
  // future keyboard shortcut.
  useEffect(() => {
    const onThreadRead = (e) => {
      const ref = e?.detail?.contact_ref;
      if (!ref) return;
      setThreads((prev) => prev.map((t) =>
        t.contact_ref === ref && (t.unread_count || 0) > 0
          ? { ...t, unread_count: 0 }
          : t,
      ));
    };
    window.addEventListener("acq:wa-thread-read", onThreadRead);
    return () => window.removeEventListener("acq:wa-thread-read", onThreadRead);
  }, []);

  useEffect(() => {
    if (activeRef) setParams({ ref: activeRef });
    else setParams({});
  }, [activeRef]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    let list = threads;
    // R51 · Specific assignee picker takes precedence over the quick tabs.
    if (assigneeId) {
      list = list.filter(x => x.assigned_to_user_id === assigneeId);
    } else if (filterAssign === "unassigned") {
      list = list.filter(x => !x.assigned_to_user_id);
    } else if (filterAssign === "mine") {
      list = list.filter(x => x.assigned_to_user_id === user?.id);
    } else if (filterAssign === "assigned") {
      list = list.filter(x => !!x.assigned_to_user_id);
    }
    // R64.2 · Read / Unread filter — orthogonal to assignment.
    if (filterRead === "unread") {
      list = list.filter(x => Number(x.unread_count || 0) > 0);
    } else if (filterRead === "read") {
      list = list.filter(x => Number(x.unread_count || 0) === 0);
    }
    // R138ca · Auto / Manual assignment-provenance filter. Legacy rows
    // without an `assigned_source` field are treated as "manual".
    if (filterSource === "auto") {
      list = list.filter(x => x.assigned_to_user_id && x.assigned_source === "auto_on_send");
    } else if (filterSource === "manual") {
      list = list.filter(x => x.assigned_to_user_id && (x.assigned_source || "manual") !== "auto_on_send");
    }
    if (!t) return list;
    return list.filter(x =>
      (x.name || "").toLowerCase().includes(t) ||
      (x.phone || "").includes(t) ||
      (x.last_text || "").toLowerCase().includes(t) ||
      (x.assigned_to_name || "").toLowerCase().includes(t));
  }, [threads, q, filterAssign, filterRead, filterSource, assigneeId, user?.id]);

  const unassignedHotCount = useMemo(() => {
    const hourAgo = Date.now() - 3600 * 1000;
    return threads.filter(t => !t.assigned_to_user_id && t.last_direction === "in" &&
                                          t.last_at && new Date(t.last_at).getTime() >= hourAgo).length;
  }, [threads]);

  return (
    <div className={embedded ? "p-2 space-y-2 flex flex-col h-full min-h-0" : "p-4 space-y-3"} data-testid="wa-inbox-page" data-compact={compact ? "true" : "false"}>
      {!compact && (
      <div className="flex items-center gap-2 flex-wrap">
        <MessageCircle className="w-5 h-5 text-primary" />
        <h1 className="text-2xl font-bold">WhatsApp inbox</h1>
        <Badge variant="outline" className="rounded-none text-[10px]" data-testid="wa-inbox-count">
          {filtered.length}{filtered.length !== threads.length ? ` of ${threads.length}` : ""} conversations
        </Badge>
        {unassignedHotCount > 0 && (
          <span className="inline-flex items-center gap-1 border border-rose-400 bg-rose-50 text-rose-800 text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5"
                   data-testid="wa-inbox-hot-count"
                   title="Unassigned threads with inbound in the last 1h">
            <Flame className="w-3 h-3" /> {unassignedHotCount} hot · unassigned
          </span>
        )}
        <div className="ml-auto flex items-center gap-1.5 flex-wrap">
          <div className="inline-flex border border-border" data-testid="wa-inbox-filter">
            {[
              { v: "all", label: "All" },
              { v: "unassigned", label: "Unassigned" },
              { v: "mine", label: "Mine" },
              { v: "assigned", label: "Assigned" },
            ].map(o => (
              <button key={o.v} type="button"
                        onClick={() => { setFilterAssign(o.v); setAssigneeId(""); }}
                        className={`text-[10px] uppercase tracking-widest font-semibold px-2 h-8 ${(!assigneeId && filterAssign === o.v) ? "bg-primary text-primary-foreground" : "bg-card text-foreground/70 hover:bg-muted/40"}`}
                        data-testid={`wa-inbox-filter-${o.v}`}>{o.label}</button>
            ))}
          </div>
          {/* R64.2 · Read/Unread pill — orthogonal to assignee, so users
              can e.g. focus on "Mine + Unread" for triage. */}
          <div className="inline-flex border border-border" data-testid="wa-inbox-read-filter">
            {[
              { v: "all",    label: "All" },
              { v: "unread", label: "Unread" },
              { v: "read",   label: "Read" },
            ].map(o => (
              <button key={o.v} type="button"
                        onClick={() => setFilterRead(o.v)}
                        className={`text-[10px] uppercase tracking-widest font-semibold px-2 h-8 ${filterRead === o.v ? "bg-primary text-primary-foreground" : "bg-card text-foreground/70 hover:bg-muted/40"}`}
                        data-testid={`wa-inbox-read-filter-${o.v}`}>{o.label}</button>
            ))}
          </div>
          {/* R138ca · Auto / Manual assignment-provenance filter — lets
              managers audit which threads were claimed via first-touch
              vs manually reassigned. */}
          <div className="inline-flex border border-border" data-testid="wa-inbox-source-filter">
            {[
              { v: "all",    label: "Any owner" },
              { v: "auto",   label: "Auto-owned" },
              { v: "manual", label: "Manual" },
            ].map(o => (
              <button key={o.v} type="button"
                        onClick={() => setFilterSource(o.v)}
                        className={`text-[10px] uppercase tracking-widest font-semibold px-2 h-8 ${filterSource === o.v ? "bg-primary text-primary-foreground" : "bg-card text-foreground/70 hover:bg-muted/40"}`}
                        data-testid={`wa-inbox-source-filter-${o.v}`}>{o.label}</button>
            ))}
          </div>
          {/* R51 · Explicit assignee picker — lets any user drill into
              another agent's queue without needing to log in as them. */}
          <select value={assigneeId}
                   onChange={(e) => setAssigneeId(e.target.value)}
                   className="h-8 border border-border bg-background text-xs px-2"
                   data-testid="wa-inbox-assignee-select"
                   title="Filter by specific assignee">
            <option value="">Any agent…</option>
            {members.map((m, i) => (
              <option key={`${m.user_id || "u"}-${i}`} value={m.user_id}>
                {(m.name || m.email || m.user_id)}{m.user_id === user?.id ? " (me)" : ""}
              </option>
            ))}
          </select>
          <Button variant="outline" size="sm" className="rounded-none h-8"
                    onClick={load} disabled={loading}
                    data-testid="wa-inbox-refresh">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
          <InteraktSyncPanel wsId={wsId} onSynced={load} />
          <BulkWaSendPanel wsId={wsId} onSent={load} />
          <InteraktTemplateEditorPanel wsId={wsId} />
          {/* R132 · Feb 2026 — Backfill legacy [template:xxx] rows */}
          <BackfillTemplateBodiesPanel wsId={wsId} onDone={load} />
        </div>
      </div>
      )}

      {/* Iter Feb 2026 R23 · Mobile-responsive layout. On <md screens the
          two-pane grid collapses to one: showing the thread list until the
          user picks a conversation, then swaps to the active thread with a
          "← Back" affordance. On ≥md screens the classic two-pane view.
          R66 · When `compact` (floating widget narrow mode) the left rail
          shrinks to 56px avatar-only column so the whole widget stays tiny. */}
      <div className={`grid grid-cols-1 ${compact ? "md:grid-cols-[56px_1fr]" : "md:grid-cols-[320px_1fr]"} gap-3 ${embedded ? "flex-1 min-h-0" : "md:h-[calc(100vh-160px)]"}`}>
        {/* Thread list */}
        <div className={`border border-border bg-card flex flex-col ${activeRef ? "hidden md:flex" : "flex"} ${embedded ? "min-h-0" : "h-[calc(100vh-220px)] md:h-full min-h-0"}`}>
          {!compact && (
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)}
                       className="h-8 rounded-none pl-7 text-xs"
                       placeholder="Search name, phone or text"
                       data-testid="wa-inbox-search" />
            </div>
          </div>
          )}
          <div className="flex-1 overflow-y-auto">
            {loading && <div className="text-xs text-muted-foreground p-4 text-center">Loading…</div>}
            {!loading && filtered.length === 0 && !compact && (
              <div className="text-xs text-muted-foreground p-6 text-center italic">
                No conversations yet — inbound WhatsApp replies land here.
              </div>
            )}
            {compact ? filtered.map(t => {
              // R66 · Compact rail — avatar + unread badge only, name shown
              // as native browser tooltip via `title`. Clicking still opens
              // the active thread.
              const initials = (t.name || t.phone || "?").trim().slice(0, 2).toUpperCase();
              const isActive = activeRef === t.contact_ref;
              return (
                <button key={t.contact_ref} type="button"
                          onClick={() => setActiveRef(t.contact_ref)}
                          className={`w-full flex justify-center py-1.5 border-b border-border hover:bg-muted/20 relative ${isActive ? "bg-primary/10 border-l-4 border-l-primary" : ""}`}
                          title={`${t.name || "(no name)"} · ${t.last_text || ""}`}
                          data-testid={`wa-thread-item-${t.contact_ref}`}>
                  <div className={`w-8 h-8 flex items-center justify-center text-[10px] font-semibold border ${t.window_open ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-border bg-muted text-muted-foreground"}`}>
                    {initials}
                  </div>
                  {t.unread_count > 0 && (
                    <span className="absolute top-0.5 right-0.5 text-[9px] px-1 py-0 bg-emerald-500 text-white rounded-full font-semibold min-w-[16px] text-center"
                             data-testid={`wa-thread-unread-${t.contact_ref}`}>
                      {t.unread_count > 9 ? "9+" : t.unread_count}
                    </span>
                  )}
                </button>
              );
            }) : filtered.map(t => {
              const isUnlinked = !t.contact_source || t.contact_source === "interakt_sync";
              const phoneDigits = ((t.phone || "").match(/\d/g) || []).length;
              const missingCc = t.phone && phoneDigits < 11;
              return (
              <div key={t.contact_ref} className="relative group">
                <button type="button"
                          onClick={() => setActiveRef(t.contact_ref)}
                          className={`w-full text-left border-b border-border p-2 hover:bg-muted/20 ${activeRef === t.contact_ref ? "bg-primary/10 border-l-4 border-l-primary" : ""}`}
                          data-testid={`wa-thread-item-${t.contact_ref}`}>
                <div className="flex items-center gap-1.5">
                  <UserIcon className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="text-xs font-semibold truncate flex-1">{t.name}</span>
                  <span className="text-[10px] text-muted-foreground">{fmtRelative(t.last_at)}</span>
                </div>
                <div className="text-[11px] text-muted-foreground truncate mt-0.5">
                  {t.last_direction === "out" && <Send className="w-2.5 h-2.5 inline mr-1" />}
                  {t.last_text || "(no messages)"}
                </div>
                <div className="flex items-center gap-1 mt-1 flex-wrap">
                  {t.window_open ? (
                    <span className="text-[9px] font-semibold uppercase tracking-widest border border-emerald-400 text-emerald-800 bg-emerald-50 px-1"
                              data-testid={`wa-thread-window-open-${t.contact_ref}`}
                              title={t.window_expires_at
                                ? `24h session window closes at ${new Date(t.window_expires_at).toLocaleString()} (${_fmtWindowRemaining(t.window_expires_at)}).`
                                : "Session window open — free-text replies allowed."}>
                      Open · {_fmtWindowRemaining(t.window_expires_at, { compact: true })}
                    </span>
                  ) : (
                    <span className="text-[9px] font-semibold uppercase tracking-widest border border-border text-muted-foreground bg-muted/30 px-1"
                              data-testid={`wa-thread-window-closed-${t.contact_ref}`}
                              title="Window closed · only an approved template can send a new message.">
                      Window closed
                    </span>
                  )}
                  {t.assigned_to_name ? (
                    <span className="text-[9px] font-semibold uppercase tracking-widest border border-indigo-400 text-indigo-800 bg-indigo-50 px-1 inline-flex items-center gap-0.5"
                             data-testid={`wa-thread-assigned-${t.contact_ref}`}>
                      <UserCheck className="w-2.5 h-2.5" /> {t.assigned_to_name}
                      {t.assigned_source === "auto_on_send" && (
                        <span className="ml-1 text-[8px] font-semibold uppercase tracking-widest border border-emerald-400 text-emerald-800 bg-emerald-50 px-0.5"
                                 data-testid={`wa-thread-auto-owned-${t.contact_ref}`}
                                 title="Auto-owned via first-touch">
                          Auto
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className="text-[9px] font-semibold uppercase tracking-widest border border-amber-400 text-amber-800 bg-amber-50 px-1"
                             data-testid={`wa-thread-unassigned-${t.contact_ref}`}>
                      Unassigned
                    </span>
                  )}
                  {missingCc && (
                    <button type="button"
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (!window.confirm(`Prepend +91 to "${t.phone}" and update every message on this thread?`)) return;
                                try {
                                  const { data: r } = await api.post(
                                    `/workspaces/${wsId}/wa/threads/${encodeURIComponent(t.contact_ref)}/fix-phone?country_code=91`);
                                  toast.success(`Fixed → ${r.new_phone}`);
                                  load?.();
                                } catch (err) { toast.error(apiErrorMessage(err, "Fix failed")); }
                              }}
                              className="text-[9px] font-semibold uppercase tracking-widest border border-amber-500 text-amber-800 bg-amber-50 hover:bg-amber-100 px-1 cursor-pointer"
                              title="Click to prepend +91 to this bare 10-digit number."
                              data-testid={`wa-thread-cc-warning-${t.contact_ref}`}>
                      ⚠ +91
                    </button>
                  )}
                  {t.unread_count > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-emerald-500 text-white rounded-full font-semibold" data-testid={`wa-thread-unread-${t.contact_ref}`}>{t.unread_count}</span>
                  )}
                  <span className="text-[9px] text-muted-foreground ml-auto font-mono">
                    ↓{t.in_count} · ↑{t.out_count}
                  </span>
                </div>
                </button>
                {/* R138t · Quick "Convert to Lead" icon for unlinked threads.
                    Sits on top of the row (absolute) — visible on hover so it
                    doesn't distract when scanning. */}
                {isUnlinked && (
                  <button type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              // Route to the thread AND signal ActiveThread to open the convert dialog.
                              setActiveRef(t.contact_ref);
                              window.dispatchEvent(new CustomEvent("wa:open-convert-lead",
                                                                                        { detail: { contact_ref: t.contact_ref }}));
                            }}
                            className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity text-[9px] font-semibold uppercase tracking-widest border border-primary text-primary bg-background px-1 hover:bg-primary/10"
                            title="Convert this WhatsApp thread into a CRM Lead"
                            data-testid={`wa-thread-convert-lead-${t.contact_ref}`}>
                    + Lead
                  </button>
                )}
              </div>
            );})}
          </div>
        </div>

        {/* Active thread */}
        <div className={`border border-border bg-card overflow-hidden ${activeRef ? "flex flex-col" : "hidden md:flex md:flex-col"} ${embedded ? "min-h-0" : "h-[calc(100vh-160px)] md:h-auto"}`}>
          {activeRef ? (
            <>
              {/* Mobile-only back button — only rendered <md */}
              <button type="button"
                       onClick={() => setActiveRef(null)}
                       className="md:hidden flex items-center gap-2 px-3 py-2 border-b border-border text-xs hover:bg-muted/40"
                       data-testid="wa-mobile-back">
                ← Back to conversations
              </button>
              <div className="flex-1 min-h-0">
                <ActiveThread wsId={wsId} contactRef={activeRef}
                                 members={members}
                                 onSent={load} onAssignChange={load} />
              </div>
            </>
          ) : (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground italic p-6 text-center">
              Pick a conversation from the left to start chatting.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function ActiveThread({ wsId, contactRef, onSent, embedded, members, onAssignChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [mentionOpen, setMentionOpen] = useState(false);
  // R138t · Feb 2026 — Convert-to-Lead dialog state.
  const [convertLeadOpen, setConvertLeadOpen] = useState(false);
  // Iter Feb 2026 R21 · Country-code prompt state — surfaces when the
  // backend returns 422 { code: 'missing_country_code' } on a WA send.
  const [ccPrompt, setCcPrompt] = useState(null); // { editUrl, retryFn, prefill }
  // Iter Feb 2026 R24 · Media attach dialog state
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);
  // R110 · File pre-loaded via drag-drop onto the composer — flows into
  // MediaPickerDialog so the user only reviews / captions / hits Send.
  const [pendingDropFile, setPendingDropFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  // R110 · Provider status pill next to Window open/closed. Cheap probe
  // via `/wa/provider-status` — no Interakt round-trip.
  const [providerStatus, setProviderStatus] = useState(null); // { configured, provider, reason?, hint? }
  // R110 · One retry in-flight at a time (avoid double-clicks + rate limits).
  const [retryingMediaMsgId, setRetryingMediaMsgId] = useState(null);
  const scrollRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(
        `/workspaces/${wsId}/wa/threads/${encodeURIComponent(contactRef)}/messages`);
      setData(r.data);
      // R51 · Mark thread read on open + broadcast so the header badge
      // AND the sidebar per-thread badge update without waiting for
      // the next poll. `acq:wa-thread-read` lets the parent list
      // optimistically zero-out that row.
      api.post(`/workspaces/${wsId}/wa/threads/${encodeURIComponent(contactRef)}/mark-read`)
         .then(() => {
           window.dispatchEvent(new CustomEvent("acq:wa-unread-refresh"));
           window.dispatchEvent(new CustomEvent("acq:wa-thread-read",
             { detail: { contact_ref: contactRef } }));
         })
         .catch(() => {});
      setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }, 50);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load thread")); }
    finally { setLoading(false); }
  };
  // Iter Feb 2026 R36 · Silent poll for new messages in the CURRENT thread
  // every 5s (visible tab only). No spinner, no scroll-jump — only scroll
  // to bottom when the message count actually increases, so the user's
  // manual scroll position is preserved while they're reading history.
  const silentReloadMessages = async () => {
    try {
      const r = await api.get(
        `/workspaces/${wsId}/wa/threads/${encodeURIComponent(contactRef)}/messages`);
      setData((prev) => {
        const prevLen = (prev?.rows || []).length;
        const nextLen = (r.data?.rows || []).length;
        if (nextLen > prevLen) {
          setTimeout(() => {
            if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
          }, 50);
        }
        return r.data;
      });
    } catch { /* silent */ }
  };
  useEffect(() => { if (wsId && contactRef) load(); }, [wsId, contactRef]);
  // R138t · Listen for row-triggered "Convert to Lead" events so clicking
  // the quick "+ Lead" button in the sidebar opens the dialog directly.
  useEffect(() => {
    const onOpen = (e) => {
      if (e?.detail?.contact_ref === contactRef) setConvertLeadOpen(true);
    };
    window.addEventListener("wa:open-convert-lead", onOpen);
    return () => window.removeEventListener("wa:open-convert-lead", onOpen);
  }, [contactRef]);
  // R110 · Load provider health once per thread mount so the pill in
  // the header reflects the workspace's real Interakt state.
  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/wa/provider-status`)
       .then((r) => setProviderStatus(r.data))
       .catch(() => setProviderStatus({ configured: false, provider: "interakt", reason: "check_failed" }));
  }, [wsId]);
  useEffect(() => {
    if (!wsId || !contactRef) return undefined;
    const iv = setInterval(() => {
      if (document.visibilityState === "visible") silentReloadMessages();
    }, 5_000);
    const onVis = () => { if (document.visibilityState === "visible") silentReloadMessages(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", onVis); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsId, contactRef]);

  const assign = async (assigneeId) => {
    setAssigning(true);
    try {
      await api.post(
        `/workspaces/${wsId}/wa/threads/${encodeURIComponent(contactRef)}/assign`,
        { assignee_id: assigneeId || null });
      toast.success(assigneeId ? "Thread assigned" : "Unassigned");
      await load();
      onAssignChange?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Assign failed"));
    } finally { setAssigning(false); }
  };

  const send = async (overridePhone) => {
    if (!text.trim()) return;
    setSending(true);
    try {
      const payload = { text: text.trim() };
      if (overridePhone) payload.override_phone = overridePhone;
      const r = await api.post(
        `/workspaces/${wsId}/wa/threads/${encodeURIComponent(contactRef)}/messages`,
        payload);
      const status = r.data.provider?.status;
      if (status === "skipped") {
        toast.warning("Interakt not configured — message recorded but not sent");
      } else if (status === "failed") {
        toast.error("Provider send failed — see logs");
      } else {
        toast.success("Sent");
      }
      // R138bz · Feb 2026 — When the backend auto-assigns this thread
      // to the sender (first-touch wins), nudge the rep so they know
      // they now own the conversation.
      if (r.data.auto_assign?.newly_assigned) {
        toast.info("You now own this WhatsApp thread — replies land in your queue.", {
          duration: 5000,
        });
      }
      setText("");
      setCcPrompt(null);
      await load();
      onSent?.();
    } catch (e) {
      // Iter Feb 2026 R21 · Detect the country-code guard 422 and surface
      // an inline prompt with a link to edit the CRM contact + a one-off
      // override input for the current send.
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 422 && detail?.code === "missing_country_code") {
        setCcPrompt({
          message: detail.message,
          editUrl: detail.hint?.edit_url,
          prefill: data?.contact?.phone || "",
          retryFn: (fixed) => send(fixed),
        });
      } else {
        toast.error(apiErrorMessage(e, "Send failed"));
      }
    } finally { setSending(false); }
  };

  // Iter 253u · Send a pre-approved Interakt template when the 24h window
  // is closed — reopens the window on success.
  // Iter 253v · Now backed by saved templates.
  // Iter 253z-5 · Also merge in the live approved template list fetched
  // directly from Interakt so ops don't need to add every approved template
  // to the local library before sending.
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [interaktLiveTemplates, setInteraktLiveTemplates] = useState([]);
  const [tplId, setTplId] = useState("");
  const [tplName, setTplName] = useState("");
  const [tplVarMap, setTplVarMap] = useState({});
  // Iter Feb 2026 R32 · Media header upload for templates that require
  // an image / video / document in the header (e.g. `edutrac_oman_jeet`).
  // Users pick a file → we POST to /wa/media/upload → the server returns
  // a public URL → we forward that URL to Interakt as headerValues[0].
  const [headerMediaUrl, setHeaderMediaUrl] = useState("");
  const [headerFilename, setHeaderFilename] = useState("");
  const [headerUploading, setHeaderUploading] = useState(false);
  const [tplSending, setTplSending] = useState(false);
  // Iter Feb 2026 R36 · Manual refresh of the Interakt live template cache
  // (Aquarius Global's account initially had no cache → composer showed
  // `[template:xxx]` instead of the rendered body and header_type
  // detection missed video-header templates like `edutrac_oman_jeet`).
  const [tplRefreshing, setTplRefreshing] = useState(false);
  // Iter Feb 2026 R36 · Toggle to expose the template composer even when
  // the 24h window is OPEN — enables sending follow-up templates in the
  // middle of an active conversation.
  const [showTplWhileOpen, setShowTplWhileOpen] = useState(false);

  const refreshInteraktTemplates = async () => {
    if (!wsId) return;
    setTplRefreshing(true);
    try {
      const { data } = await api.get(`/workspaces/${wsId}/integrations/interakt/templates`);
      setInteraktLiveTemplates(data?.rows || []);
      toast.success(`Refreshed ${(data?.rows || []).length} Interakt templates`);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to refresh templates"));
    } finally { setTplRefreshing(false); }
  };

  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/wa/saved-templates`)
       .then((r) => setSavedTemplates(r.data || []))
       .catch(() => {});
    api.get(`/workspaces/${wsId}/integrations/interakt/templates`)
       .then((r) => setInteraktLiveTemplates(r.data?.rows || []))
       .catch(() => {});
  }, [wsId]);

  const activeTpl = savedTemplates.find((t) => t.id === tplId);
  useEffect(() => {
    // Reset variables when switching templates.
    if (activeTpl) {
      const seed = {};
      (activeTpl.variables || []).forEach((v, i) => { seed[i] = v.placeholder || ""; });
      setTplVarMap(seed);
      setTplName(activeTpl.interakt_template_name);
      // R32 · Reset media state on template switch
      setHeaderMediaUrl("");
      setHeaderFilename("");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tplId]);

  // R32 · Media requirement is derived from either the saved template's
  // `header_type` or the interakt live template shape.
  // Iter Feb 2026 R36 · Also match by the raw `tplName` typed into the
  // free-text input, not just via `activeTpl?.interakt_template_name`.
  // Previously typing `edutrac_oman_jeet` directly bypassed detection so
  // the send failed at Interakt with "Media Url is missing for header's
  // video". Now the video-header uploader appears as soon as the name
  // (whether picked or typed) matches any known live template.
  const _tplLookupName = (activeTpl?.interakt_template_name || tplName || "").trim().toLowerCase();
  const _matchedLive = interaktLiveTemplates.find(
    (t) => (t.name || t.template_name || "").toLowerCase() === _tplLookupName,
  );
  const headerType = (activeTpl?.header_type
                        || _matchedLive?.header_type
                        || "").toUpperCase();
  const needsMediaHeader = ["IMAGE", "VIDEO", "DOCUMENT"].includes(headerType);

  const uploadHeaderMedia = async (file) => {
    if (!file) return;
    setHeaderUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/workspaces/${wsId}/wa/media/upload`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setHeaderMediaUrl(data.public_url || data.url || "");
      setHeaderFilename(file.name);
      toast.success("Media uploaded — ready to send");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Media upload failed"));
    } finally { setHeaderUploading(false); }
  };

  // Iter Feb 2026 R49 · Inline retry-with-media banner on failed
  // template messages. If Interakt rejected the send with a
  // "Media Url is missing" error, agents can pick a file right on the
  // failed bubble to upload + re-send the same template without
  // having to re-select it from the composer.
  const [retryingMediaFor, setRetryingMediaFor] = useState(null);
  const retryTemplateWithMedia = async (msg, file) => {
    if (!file) return;
    setRetryingMediaFor(msg.id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post(
        `/workspaces/${wsId}/wa/media/upload`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      const url = up.data.public_url || up.data.url;
      if (!url) throw new Error("upload returned no URL");
      const r = await api.post(
        `/workspaces/${wsId}/wa/threads/${encodeURIComponent(contactRef)}/send-template`,
        {
          template_name: msg.template_name,
          body_vars: msg.template_vars || [],
          header_media_url: url,
          header_filename: file.name,
        });
      if (r.data?.provider?.status === "sent") {
        toast.success(`Template "${msg.template_name}" re-sent with media`);
        await load();
      } else {
        const prov = r.data?.provider || {};
        toast.error(`Retry failed — ${prov.reason || prov.detail || "unknown"}`, { duration: 8000 });
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, "Retry failed"));
    } finally { setRetryingMediaFor(null); }
  };

  // R110 · Feb 2026 — Retry a failed OUTBOUND media send. The file
  // already lives in our object storage (permanent `wa_media` row),
  // so a retry is a single `/send-media` call with the same media_id
  // + caption. No re-upload needed → truly one-click retry for
  // transient Interakt hiccups.
  const retryFailedMedia = async (msg) => {
    if (!msg?.media_id) {
      toast.error("This message has no attached media to retry.");
      return;
    }
    setRetryingMediaMsgId(msg.id);
    try {
      const r = await api.post(
        `/workspaces/${wsId}/wa/threads/${encodeURIComponent(contactRef)}/send-media`,
        { media_id: msg.media_id, caption: msg.text || "" });
      const prov = r.data?.provider || {};
      if (prov.status === "sent") {
        toast.success("Media re-sent");
        await load();
      } else if (prov.status === "skipped") {
        toast.error(prov.reason === "interakt_not_configured"
                        ? "Interakt is not configured — connect it in Settings → Integrations."
                        : `Retry skipped (${prov.reason || "unknown"})`);
      } else {
        const hint = prov.detail || prov.error || prov.reason || "Provider rejected the send.";
        toast.error(`Retry failed — ${hint}`, { duration: 6000 });
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, "Retry failed"));
    } finally { setRetryingMediaMsgId(null); }
  };

  const sendTemplate = async () => {
    const rawName = (activeTpl?.interakt_template_name || tplName || "").trim();
    if (!rawName) return;
    setTplSending(true);
    try {
      // Vars in template ordinal order.
      const vars = activeTpl
        ? (activeTpl.variables || []).map((_, i) => (tplVarMap[i] || "").trim()).filter(Boolean)
        : [];
      const sendPayload = { template_name: rawName, body_vars: vars };
      // R32 · Attach the uploaded media URL for templates that
      // require a header IMAGE/VIDEO/DOCUMENT.
      if (headerMediaUrl) {
        sendPayload.header_media_url = headerMediaUrl;
        if (headerFilename) sendPayload.header_filename = headerFilename;
      }
      const r = await api.post(
        `/workspaces/${wsId}/wa/threads/${encodeURIComponent(contactRef)}/send-template`,
        sendPayload);
      const status = r.data.provider?.status;
      if (status === "sent") {
        toast.success(`Template "${activeTpl?.name || rawName}" sent — 24h window re-opened`);
      } else {        // Feb 2026 R6 · Surface Interakt's real error text (e.g. "No approved
        // template found with name '...'", "template variables mismatch")
        // instead of a generic message the user can't act on.
        const prov = r.data.provider || {};
        const bits = [];
        if (prov.reason) bits.push(prov.reason);
        if (prov.provider_status) bits.push(`HTTP ${prov.provider_status}`);
        if (prov.detail) {
          try {
            const parsed = JSON.parse(prov.detail);
            if (parsed?.message) bits.push(parsed.message);
            else bits.push(prov.detail);
          } catch { bits.push(prov.detail); }
        }
        toast.error(
          bits.length > 0
            ? `Template send failed — ${bits.join(" · ")}`
            : "Template send failed — check the template name in Interakt",
          { duration: 10000 },
        );
      }
      // R138bz · Feb 2026 — Auto-assign toast on template-send too.
      if (r.data.auto_assign?.newly_assigned) {
        toast.info("You now own this WhatsApp thread — replies land in your queue.", {
          duration: 5000,
        });
      }
      setTplId(""); setTplName(""); setTplVarMap({});
      await load();
      onSent?.();
    } catch (e) {
      // Iter Feb 2026 R21 · Same country-code guard on template send
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 422 && detail?.code === "missing_country_code") {
        setCcPrompt({
          message: detail.message,
          editUrl: detail.hint?.edit_url,
          prefill: data?.contact?.phone || "",
          retryFn: (fixed) => {
            // Retry the same template send with override
            setTplSending(true);
            const rawName = tplName;
            const vars = activeTpl
              ? (activeTpl.variables || []).map((_, i) => (tplVarMap[i] || "").trim()).filter(Boolean)
              : [];
            return api.post(
              `/workspaces/${wsId}/wa/threads/${encodeURIComponent(contactRef)}/send-template`,
              { template_name: rawName, body_vars: vars, override_phone: fixed })
              .then(() => { toast.success("Template sent"); setCcPrompt(null); load(); onSent?.(); })
              .catch((e2) => toast.error(apiErrorMessage(e2, "Retry failed")))
              .finally(() => setTplSending(false));
          },
        });
      } else if (e?.response?.status === 422 && detail?.code === "media_header_required") {
        // Iter Feb 2026 R49 · Backend pre-flight caught a missing media header
        // (e.g. `edutrac_oman_jeet` requires VIDEO). Auto-refresh Interakt
        // template cache so the uploader appears on the next retry.
        toast.error(
          detail.message + " " + (detail.hint || ""),
          { duration: 10000 },
        );
        try { await refreshInteraktTemplates(); } catch { /* refresh handles its own toast */ }
      } else if (e?.response?.status === 502) {
        // Iter Feb 2026 R49 · Interakt-side send failure. Backend already
        // persisted the failed attempt to wa_messages, so reload messages
        // to render the inline retry banner. Also toast the reason.
        // R57 · If the backend detected the required header_type from
        // Interakt's error text, auto-refresh templates so the next
        // send-attempt shows the media uploader BEFORE the user clicks
        // Send (fixing the "sends first, then asks for media" flow).
        const prov = detail || {};
        const probe = prov.media_probe;
        let msg = prov.provider_response || prov.message || "Interakt error";
        if (probe && probe.ok === false) {
          msg = `Media URL unreachable (${probe.status_code || probe.error}) — ${prov.hint || ""}`;
        } else if (prov.detected_header_type && !headerType) {
          msg = `${prov.detected_header_type} header required — uploader will appear on retry.`;
        }
        toast.error(`Template send failed — ${msg}`, { duration: 10000 });
        await load();
        if (prov.detected_header_type) {
          try { await refreshInteraktTemplates(); } catch { /* handled */ }
        }
      } else {
        toast.error(apiErrorMessage(e, "Template send failed"));
      }
    } finally { setTplSending(false); }
  };

  if (loading || !data) {
    return <div className="p-8 text-center text-xs text-muted-foreground">Loading conversation…</div>;
  }

  const win = data.window || { open: false };

  return (
    <div className="h-full flex flex-col" data-testid="wa-active-thread">
      {!embedded && (
        <div className="border-b border-border p-3 flex items-center gap-2 flex-wrap">
          <UserIcon className="w-4 h-4 text-primary" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate">{data.contact.name}</div>
            <div className="text-[10px] text-muted-foreground flex items-center gap-1.5 flex-wrap">
              <span>{data.contact.phone || contactRef}{data.contact.email && ` · ${data.contact.email}`}</span>
              {/* R138t · Country-code sanity chip — surfaces bare 10-digit
                  phones (Interakt-sync artefact) so users know the number
                  is missing +91 and replies will fail. One-click fix:
                  prepends +91 and refreshes the thread. */}
              {(() => {
                const p = data.contact.phone || "";
                const digits = (p.match(/\d/g) || []).length;
                if (!p || digits >= 11) return null;
                return (
                  <button type="button"
                            onClick={async () => {
                              if (!window.confirm(`Prepend +91 to "${p}" and update every message on this thread?`)) return;
                              try {
                                const { data: r } = await api.post(
                                  `/workspaces/${wsId}/wa/threads/${encodeURIComponent(contactRef)}/fix-phone?country_code=91`);
                                toast.success(`Fixed → ${r.new_phone} · ${r.messages_updated} message${r.messages_updated === 1 ? "" : "s"} updated`);
                                onSent?.();
                              } catch (e) { toast.error(apiErrorMessage(e, "Fix failed")); }
                            }}
                            className="text-[9px] font-semibold uppercase tracking-widest border border-amber-500 text-amber-800 bg-amber-50 hover:bg-amber-100 px-1 cursor-pointer"
                            title="Click to prepend +91 to this bare 10-digit number and re-link every message."
                            data-testid="wa-thread-cc-warning">
                    ⚠ Verify · fix +91
                  </button>
                );
              })()}
            </div>
          </div>
          {/* R138t · Convert-to-Lead button — visible only when the thread
              is NOT already linked to a CRM record. Clicking creates a
              new Lead pre-filled with the WA thread's name/phone and
              re-links every message to it. */}
          {!data.contact.contact_source && (
            <Button size="sm" variant="outline"
                      className="rounded-none h-7 px-2 text-[10px] border-primary text-primary hover:bg-primary/10"
                      onClick={() => setConvertLeadOpen(true)}
                      data-testid="wa-thread-convert-lead-btn">
              <UserPlus className="w-3 h-3 mr-1" /> Convert to Lead
            </Button>
          )}
          {/* Assignee picker */}
          <div className="flex items-center gap-1" data-testid="wa-thread-assign">
            <UserPlus className="w-3.5 h-3.5 text-muted-foreground" />
            <Select value={data.assigned_to_user_id || "__none__"}
                       onValueChange={(v) => assign(v === "__none__" ? null : v)}
                       disabled={assigning || !(members && members.length)}>
              <SelectTrigger className="h-7 w-40 rounded-none text-[11px]" data-testid="wa-thread-assign-trigger">
                <SelectValue placeholder="Assign to…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Unassigned</SelectItem>
                {(members || []).map((m) => (
                  <SelectItem key={m.user_id} value={m.user_id}>
                    {(m.user && (m.user.name || m.user.email)) || m.user_id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" className="rounded-none h-7 px-2 text-[10px]"
                      onClick={() => setMentionOpen(true)}
                      data-testid="wa-thread-mention-open">
              <AtSign className="w-3 h-3 mr-1" /> Mention
            </Button>
            {/* Iter Feb 2026 R22 · Delete conversation. Confirms twice. */}
            <Button size="sm" variant="outline" className="rounded-none h-7 px-2 text-[10px] text-rose-700 border-rose-300 hover:bg-rose-50"
                      onClick={async () => {
                        if (!window.confirm("Delete this entire conversation? All messages + assignment state will be permanently removed. This cannot be undone.")) return;
                        if (!window.confirm("Really delete? Type YES via a second confirm — one more click and it's gone.")) return;
                        try {
                          await api.delete(`/workspaces/${wsId}/wa/threads/${encodeURIComponent(contactRef)}`);
                          toast.success("Conversation deleted");
                          onSent?.();
                        } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
                      }}
                      data-testid="wa-thread-delete">
              <Trash2 className="w-3 h-3 mr-1" /> Delete
            </Button>
          </div>
          {win.open ? (
            <span className="text-[10px] font-semibold uppercase tracking-widest border border-emerald-400 text-emerald-800 bg-emerald-50 px-1.5 py-0.5"
                     data-testid="wa-window-badge-open"
                     title={win.expires_at
                       ? `24h session window closes at ${new Date(win.expires_at).toLocaleString()} (${_fmtWindowRemaining(win.expires_at)}).`
                       : "Session window is open — free-text replies allowed."}>
              Window open · closes {_fmtWindowRemaining(win.expires_at)}
            </span>
          ) : (
            <span className="text-[10px] font-semibold uppercase tracking-widest border border-rose-400 text-rose-800 bg-rose-50 px-1.5 py-0.5"
                     data-testid="wa-window-badge-closed"
                     title={win.last_inbound_at
                       ? `Last inbound ${new Date(win.last_inbound_at).toLocaleString()} · Only an approved template can re-open the 24h session window.`
                       : "No inbound reply yet · Only an approved template can start a new conversation."}>
              Window closed · needs template
            </span>
          )}
          {/* R110 · Live Interakt provider status. Green when configured,
              amber when unknown/checking, red when the workspace hasn't
              connected Interakt yet — with a jump link to Settings. */}
          {providerStatus === null ? (
            <span className="text-[10px] font-semibold uppercase tracking-widest border border-border text-muted-foreground bg-muted/30 px-1.5 py-0.5"
                  data-testid="wa-provider-status-loading">
              Interakt · checking…
            </span>
          ) : providerStatus.configured ? (
            <span className="text-[10px] font-semibold uppercase tracking-widest border border-emerald-400 text-emerald-800 bg-emerald-50 px-1.5 py-0.5"
                  data-testid="wa-provider-status-ok"
                  title="Interakt credentials found — outbound sends will hit the provider.">
              Interakt OK
            </span>
          ) : (
            <Link to="/app/settings?section=integrations"
                  className="text-[10px] font-semibold uppercase tracking-widest border border-rose-400 text-rose-800 bg-rose-50 px-1.5 py-0.5 hover:bg-rose-100"
                  data-testid="wa-provider-status-off"
                  title={providerStatus.hint || "Interakt is not configured for this workspace."}>
              Interakt not connected
            </Link>
          )}
        </div>
      )}

      <div ref={scrollRef} className={`flex-1 overflow-y-auto ${embedded ? "max-h-72" : ""} p-3 space-y-1.5 bg-[#e5ddd5]/40`}>
        {data.rows.length === 0 && (
          <div className="text-center text-xs text-muted-foreground p-6 italic">
            No messages yet.
          </div>
        )}
        {data.rows.map(m => (
          m.direction === "system" ? (
            // R138bz · Feb 2026 — System audit rows (assignment notes,
            // etc.) render as a centred subtle chip so they don't look
            // like a real inbound/outbound bubble.
            <div key={m.id}
                 className="flex justify-center my-1"
                 data-testid={`wa-msg-system-${m.id}`}>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground bg-muted/40 border border-border px-2 py-0.5 rounded-full">
                {typeof m.text === "string" && m.text ? m.text : "System event"}
              </div>
            </div>
          ) : (
          <div key={m.id}
                  className={`flex ${m.direction === "out" ? "justify-end" : "justify-start"}`}
                  data-testid={`wa-msg-${m.id}`}>
            <div className={`max-w-[70%] px-2 py-1.5 shadow-sm text-xs ${m.direction === "out" ? "bg-emerald-100 border border-emerald-300" : "bg-white border border-border"}`}>
              {/* Iter Feb 2026 R24 · Media preview (image/doc/video/audio) */}
              {m.media_id && (
                <>
                  <MediaBubble mediaId={m.media_id} kind={m.media_kind}
                                      contentType={m.media_content_type}
                                      filename={m.media_filename} />
                  {/* R110 · Feb 2026 — Retry action on failed outbound
                      media so users don't need to re-upload after a
                      transient Interakt hiccup. Backend already stores
                      the file in permanent storage, so a retry is just
                      `POST /send-media` with the same `media_id`. */}
                  {m.direction === "out" && m.status === "failed" && (
                    <button
                      type="button"
                      onClick={() => retryFailedMedia(m)}
                      disabled={retryingMediaMsgId === m.id}
                      className="mt-1 text-[10px] text-primary hover:underline inline-flex items-center gap-1"
                      data-testid={`wa-msg-retry-media-btn-${m.id}`}
                    >
                      {retryingMediaMsgId === m.id
                        ? <><Loader2 className="w-3 h-3 animate-spin" /> Retrying…</>
                        : <><RefreshCw className="w-3 h-3" /> Retry send</>}
                    </button>
                  )}
                </>
              )}
              {/* Iter Feb 2026 R22 · Graceful fallback for corrupted historic
                    rows where `text` was inserted as a list/dict (rendered
                    literally as `[]` / `{}` / `[object Object]`).
                    R28 · If `message_type` is set (sticker, image, voice,
                    document, location, contacts, interactive, reaction,
                    order, unsupported), show the placeholder text — this
                    is populated by the extractor for messages that carry
                    no plain text (all of Shreyas's replies fell here). */}
              <div className="whitespace-pre-wrap break-words">
                {(() => {
                  const t = m.text;
                  // R131 · If we already stored the rendered template body,
                  // prefer that over any placeholder text.
                  if (m.template_body_rendered) return m.template_body_rendered;
                  // Legacy rows: text is `[template:xxx]` but template_name
                  // is set — lazy-fetch the body from the server.
                  if (typeof t === "string" && /^\[template:.+\]$/i.test(t.trim()) && m.template_name) {
                    return <TemplateBodyPreview wsId={wsId} name={m.template_name}
                                                 vars={m.template_vars || []}
                                                 fallback={t} />;
                  }
                  if (typeof t === "string" && t.trim() && t.trim() !== "[]" && t.trim() !== "{}" && t.trim() !== "[object Object]") {
                    return t;
                  }
                  if (m.message_type) {
                    return <span className="italic text-muted-foreground">({m.message_type} message)</span>;
                  }
                  if (!t) return <span className="italic text-muted-foreground">(empty)</span>;
                  if (typeof t !== "string") return <span className="italic text-muted-foreground">(non-text message · check webhook logs)</span>;
                  return <span className="italic text-muted-foreground">(non-text message · likely interactive/reaction)</span>;
                })()}
              </div>
              {/* R28 · Show the failure reason directly on the outbound
                    row so users can act on it (invalid number, template
                    not approved, no active WA account, etc). Populated
                    by the delivery-status webhook update. */}
              {m.direction === "out" && m.status === "failed" && (m.error_message || m.error_code) && (
                <div className="mt-1 text-[10px] text-rose-800 bg-rose-50 border border-rose-200 px-1.5 py-1 rounded"
                      data-testid={`wa-msg-failure-${m.id}`}>
                  <span className="font-semibold">Failed</span>
                  {m.error_code ? <span> · {m.error_code}</span> : null}
                  {m.error_message ? <span> · {m.error_message}</span> : null}
                  {/* Iter Feb 2026 R49 · Inline retry-with-media affordance.
                        Triggered when Interakt / our backend flagged the
                        send as failing because of a missing media header. */}
                  {m.template_name && (
                    /media url is missing|media_header_required|media header/i.test(
                      `${m.error_message || ""} ${m.error_code || ""}`
                    )
                  ) && (
                    <div className="mt-1 pt-1 border-t border-rose-200 flex items-center gap-2"
                          data-testid={`wa-msg-retry-media-${m.id}`}>
                      <span className="font-semibold">Media required</span>
                      <label className="cursor-pointer text-rose-700 underline hover:text-rose-900">
                        <input type="file"
                                accept="image/*,video/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                className="hidden"
                                disabled={retryingMediaFor === m.id}
                                onChange={(e) => retryTemplateWithMedia(m, e.target.files?.[0])}
                                data-testid={`wa-msg-retry-media-file-${m.id}`} />
                        {retryingMediaFor === m.id ? "Uploading & retrying…" : "Click to upload & retry"}
                      </label>
                    </div>
                  )}
                </div>
              )}
              {/* Iter Feb 2026 R29 · Render template buttons as chips so
                    agents can see exactly which quick-reply / URL / phone
                    buttons the customer received. This closes the "I don't
                    know what content was sent" gap the user flagged.
                    R50 · URL / PHONE_NUMBER buttons are now actual links
                    (open in new tab / dial) so agents can open the URL to
                    verify what the customer will see. QUICK_REPLY chips
                    remain visual-only. */}
              {Array.isArray(m.template_buttons) && m.template_buttons.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1 border-t border-black/10 pt-1.5"
                      data-testid={`wa-msg-template-buttons-${m.id}`}>
                  {m.template_buttons.map((b, i) => {
                    const label = b.text || "(button)";
                    const cls = "text-[10px] font-medium bg-white/80 border border-current/40 rounded-full px-2 py-0.5 inline-flex items-center gap-1";
                    if (b.type === "URL" && b.url) {
                      return (
                        <a key={i} href={b.url} target="_blank" rel="noopener noreferrer"
                           className={`${cls} hover:bg-sky-50 hover:border-sky-400 text-sky-700 no-underline`}
                           data-testid={`wa-msg-template-button-${m.id}-${i}`}
                           title={b.url}>
                          🔗 {label}
                        </a>
                      );
                    }
                    if (b.type === "PHONE_NUMBER" && b.phone_number) {
                      return (
                        <a key={i} href={`tel:${b.phone_number}`}
                           className={`${cls} hover:bg-emerald-50 hover:border-emerald-400 text-emerald-700 no-underline`}
                           data-testid={`wa-msg-template-button-${m.id}-${i}`}
                           title={b.phone_number}>
                          📞 {label}
                        </a>
                      );
                    }
                    return (
                      <span key={i} className={cls}
                             data-testid={`wa-msg-template-button-${m.id}-${i}`}>
                        {b.type === "OTP" ? "🔒 " : ""}{label}
                      </span>
                    );
                  })}
                </div>
              )}
              {m.template_name && (
                <div className="mt-1 text-[9px] text-muted-foreground/80 italic"
                      data-testid={`wa-msg-template-tag-${m.id}`}>
                  Template · {m.template_name}
                </div>
              )}
              <div className="flex items-center gap-1 mt-0.5 text-[9px] text-muted-foreground justify-end">
                {m.agent_name && <span>{m.agent_name}</span>}
                <span>{fmtRelative(m.created_at)}</span>
                {m.direction === "out" && (
                  m.status === "read" ? <CheckCheck className="w-3 h-3 text-sky-500" />
                    : m.status === "delivered" ? <CheckCheck className="w-3 h-3" />
                    : m.status === "failed" ? <ShieldAlert className="w-3 h-3 text-rose-500" />
                    : <Check className="w-3 h-3" />
                )}
              </div>
            </div>
          </div>
          )
        ))}
      </div>

      <div className={`border-t border-border p-2 space-y-1.5 relative ${dragOver ? "ring-2 ring-primary ring-inset bg-primary/5" : ""}`}
           onDragEnter={(e) => {
             if (!win.open) return;
             const hasFiles = Array.from(e.dataTransfer?.items || []).some((it) => it.kind === "file");
             if (!hasFiles) return;
             e.preventDefault(); e.stopPropagation();
             setDragOver(true);
           }}
           onDragOver={(e) => {
             if (!win.open) return;
             const hasFiles = Array.from(e.dataTransfer?.items || []).some((it) => it.kind === "file");
             if (!hasFiles) return;
             e.preventDefault(); e.stopPropagation();
             e.dataTransfer.dropEffect = "copy";
           }}
           onDragLeave={(e) => {
             // Only end when leaving the composer bounds — nested drags trigger dragleave too.
             if (e.currentTarget.contains(e.relatedTarget)) return;
             setDragOver(false);
           }}
           onDrop={(e) => {
             if (!win.open) return;
             const file = e.dataTransfer?.files?.[0];
             if (!file) return;
             e.preventDefault(); e.stopPropagation();
             setDragOver(false);
             // R110 · Feed the dropped file straight into the MediaPickerDialog
             // so the user can add a caption + confirm before send. Skips the
             // extra "click to pick a file" step that used to be paperclip → modal.
             setPendingDropFile(file);
             setMediaPickerOpen(true);
           }}
           data-testid="wa-composer-dropzone">
        {dragOver && (
          <div className="absolute inset-0 z-30 pointer-events-none flex items-center justify-center bg-primary/10 border-2 border-dashed border-primary"
               data-testid="wa-composer-drop-overlay">
            <div className="text-xs font-semibold text-primary bg-background/95 px-3 py-1.5 border border-primary shadow">
              Drop to attach &amp; send
            </div>
          </div>
        )}
        {/* Iter Feb 2026 R36 · Template composer — always available.
             When the 24h reply window is CLOSED we surface it prominently
             with a rose-colored callout (mandatory action).
             When the window is OPEN we hide it behind a small "Send
             template" toggle so it doesn't dominate the composer but is
             still reachable for follow-up templates. */}
        {(!win.open || showTplWhileOpen) && (
          <div className={`text-[10px] px-2 py-1 space-y-1.5 ${
              !win.open
                ? "text-rose-800 bg-rose-50 border border-rose-300"
                : "text-foreground bg-muted/40 border border-border"
            }`} data-testid="wa-template-block">
            <div className="flex items-center gap-1 justify-between">
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {!win.open
                  ? "24h reply window is closed. Send an approved template to re-open the conversation."
                  : "Send another approved template inside the open window."}
              </div>
              {/* R36 · Manual refresh of the Interakt live template cache
                     so users can pull templates approved AFTER page load
                     (or first-time onboarding of a workspace with an
                     empty cache like Aquarius Global). */}
              <button
                type="button"
                onClick={refreshInteraktTemplates}
                disabled={tplRefreshing}
                className="text-[10px] font-medium hover:underline disabled:opacity-50 flex items-center gap-1"
                data-testid="wa-template-refresh"
                title="Re-fetch approved templates from Interakt"
              >
                <RefreshCw className={`w-3 h-3 ${tplRefreshing ? "animate-spin" : ""}`} />
                {tplRefreshing ? "Refreshing…" : "Refresh"}
              </button>
            </div>
            <div className="space-y-1.5" data-testid="wa-template-picker">
              {(savedTemplates.length > 0 || interaktLiveTemplates.length > 0) && (
                <select value={tplId || (tplName ? `__live__:${tplName}` : "")}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v.startsWith("__live__:")) {
                            setTplId("");
                            setTplName(v.slice("__live__:".length));
                          } else {
                            setTplId(v);
                          }
                          // R59 · Auto-scroll to media uploader after
                          // template pick so agents SEE the required-file
                          // banner immediately instead of hitting Send blind.
                          setTimeout(() => {
                            const el = document.querySelector('[data-testid="wa-template-media-header"]');
                            if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
                          }, 100);
                        }}
                        className="w-full h-7 text-xs px-2 border border-border bg-background rounded-none"
                        data-testid="wa-template-select">
                  <option value="">— Pick a template —</option>
                  {savedTemplates.length > 0 && (
                    <optgroup label="Saved templates (library)">
                      {savedTemplates.map((t) => {
                        // R59 · Prefix option label with a media-type chip
                        // so agents can identify what file they'll need
                        // BEFORE hitting Send.
                        const ht = (t.header_type || "").toUpperCase();
                        const chip = ht === "IMAGE" ? "🖼 IMG  · "
                                    : ht === "VIDEO" ? "🎥 VID  · "
                                    : ht === "DOCUMENT" ? "📄 PDF · "
                                    : "";
                        return (
                          <option key={t.id} value={t.id}>{chip}{t.name}</option>
                        );
                      })}
                    </optgroup>
                  )}
                  {interaktLiveTemplates.length > 0 && (
                    <optgroup label="Approved on Interakt">
                      {interaktLiveTemplates.map((t) => {
                        const ht = (t.header_type || "").toUpperCase();
                        const chip = ht === "IMAGE" ? "🖼 IMG · "
                                    : ht === "VIDEO" ? "🎥 VID · "
                                    : ht === "DOCUMENT" ? "📄 PDF · "
                                    : "";
                        return (
                          <option key={t.name || t.template_name}
                                    value={`__live__:${t.name || t.template_name}`}>
                            {chip}{(t.name || t.template_name)}
                            {t.language ? ` · ${t.language}` : ""}
                            {t.category ? ` [${t.category}]` : ""}
                          </option>
                        );
                      })}
                    </optgroup>
                  )}
                </select>
              )}
              {!activeTpl && (
                <input type="text" value={tplName}
                       onChange={(e) => setTplName(e.target.value)}
                       placeholder={(savedTemplates.length || interaktLiveTemplates.length) ? "…or raw Interakt template name" : "Interakt template name (e.g. hello_world)"}
                       className="w-full h-7 text-xs px-2 border border-border bg-background rounded-none"
                       data-testid="wa-template-name" />
              )}
              {activeTpl && (activeTpl.variables || []).length > 0 && (
                <div className="space-y-1">
                  {activeTpl.variables.map((v, i) => (
                    <div key={i} className="flex items-center gap-1">
                      <span className="text-[10px] font-mono text-muted-foreground w-8">{`{{${i + 1}}}`}</span>
                      <input type="text"
                             value={tplVarMap[i] || ""}
                             onChange={(e) => setTplVarMap({ ...tplVarMap, [i]: e.target.value })}
                             placeholder={v.label}
                             className="flex-1 h-7 text-xs px-2 border border-border bg-background rounded-none"
                             data-testid={`wa-template-var-${i}`} />
                    </div>
                  ))}
                </div>
              )}
              {/* Iter Feb 2026 R32 · Media header uploader for templates
                    that require an image / video / document in the
                    header. `edutrac_oman_jeet` is a video-header
                    template and previously errored with "video URL
                    required" because there was no way to pass one.
                    R57 · Prominent banner when file is required-but-missing
                    so users can't accidentally hit Send prematurely. */}
              {needsMediaHeader && (
                <div className={`border-2 p-3 rounded-none space-y-2 ${headerMediaUrl ? "border-emerald-500/40 bg-emerald-50/40" : "border-rose-500/60 bg-rose-50 shadow-sm"}`}
                      data-testid="wa-template-media-header">
                  <div className={`text-xs font-bold flex items-center gap-2 ${headerMediaUrl ? "text-emerald-800" : "text-rose-800"}`}>
                    {headerType === "IMAGE" && <ImageIcon className="w-4 h-4" />}
                    {headerType === "VIDEO" && <Film className="w-4 h-4" />}
                    {headerType === "DOCUMENT" && <FileText className="w-4 h-4" />}
                    {headerMediaUrl
                      ? <span>✓ {headerType} attached — ready to send</span>
                      : <span>
                          This template needs a <span className="underline">{headerType.toLowerCase()}</span> file to send
                        </span>}
                  </div>
                  {!headerMediaUrl && (
                    <div className="text-[10px] text-rose-700/80">
                      Accepted: {headerType === "IMAGE" ? "JPG, PNG, WebP (≤ 5 MB)"
                                : headerType === "VIDEO" ? "MP4, 3GP (≤ 16 MB, H.264 recommended)"
                                : "PDF, DOC, DOCX, XLS, XLSX (≤ 100 MB)"}
                    </div>
                  )}
                  {!headerMediaUrl ? (
                    <label className="block w-full border border-dashed border-rose-500/60 bg-white p-2 text-center cursor-pointer hover:bg-rose-50 transition-colors">
                      <Upload className="w-4 h-4 mx-auto mb-1 text-rose-600" />
                      <div className="text-[11px] font-medium text-rose-700">
                        {headerUploading ? "Uploading…" : "Choose file to attach"}
                      </div>
                      <input type="file"
                              accept={
                                headerType === "IMAGE" ? "image/*"
                                : headerType === "VIDEO" ? "video/*"
                                : "application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                              }
                              disabled={headerUploading}
                              onChange={(e) => uploadHeaderMedia(e.target.files?.[0])}
                              className="sr-only"
                              data-testid="wa-template-header-file" />
                    </label>
                  ) : (
                    <div className="flex items-center gap-2 text-[11px]">
                      <span className="text-emerald-700 font-medium truncate max-w-[240px]" title={headerFilename}>
                        ✓ {headerFilename || "Media uploaded"}
                      </span>
                      <button type="button"
                               className="text-[10px] text-rose-700 hover:underline"
                               onClick={() => { setHeaderMediaUrl(""); setHeaderFilename(""); }}
                               data-testid="wa-template-header-clear">
                        replace
                      </button>
                    </div>
                  )}
                </div>
              )}
              <div className="flex justify-end">
                <Button size="sm" className="h-7 rounded-none text-xs"
                        onClick={sendTemplate}
                        disabled={tplSending
                                       || !((activeTpl?.interakt_template_name || tplName || "").trim())
                                       || (needsMediaHeader && !headerMediaUrl)}
                        data-testid="wa-template-send">
                  {tplSending ? <Loader2 className="w-3 h-3 animate-spin" />
                    : (needsMediaHeader && !headerMediaUrl)
                      ? `Attach ${headerType.toLowerCase()} first`
                      : "Send template"}
                </Button>
              </div>
            </div>
          </div>
        )}
        <div className="flex items-center gap-1.5">
          {/* Iter Feb 2026 R24 · Media attach button — opens the picker. */}
          <button type="button"
                   className="h-9 px-2 border border-border rounded-none hover:bg-muted/50 disabled:opacity-50"
                   disabled={!win.open || sending}
                   onClick={() => setMediaPickerOpen(true)}
                   title="Attach media (image · PDF · doc · video · voice note)"
                   data-testid="wa-composer-media">
            <Paperclip className="w-3.5 h-3.5" />
          </button>
          {/* Iter Feb 2026 R36 · "Send template" toggle — makes the
               template composer reachable in the middle of an open 24h
               window (previously buried and only shown when closed). */}
          {win.open && (
            <button type="button"
                    className={`h-9 px-2 border rounded-none hover:bg-muted/50 flex items-center gap-1 text-[11px] font-medium ${
                      showTplWhileOpen ? "border-primary text-primary bg-primary/5" : "border-border"
                    }`}
                    onClick={() => setShowTplWhileOpen((s) => !s)}
                    title="Send an approved template inside the open window"
                    data-testid="wa-composer-template-toggle">
              <FileText className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Template</span>
            </button>
          )}
          <Textarea rows={1} value={text}
                        onChange={(e) => setText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault(); send();
                          }
                        }}
                        placeholder={win.open ? "Type your reply — Enter to send"
                                                    : "Window closed — send a Campaign template instead"}
                        disabled={!win.open || sending}
                        className="rounded-none text-xs min-h-9"
                        data-testid="wa-composer-input" />
          <Button size="sm" className="rounded-none h-9" onClick={() => send()}
                    disabled={!win.open || sending || !text.trim()}
                    data-testid="wa-composer-send">
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>

      {mediaPickerOpen && (
        <MediaPickerDialog
          wsId={wsId}
          contactRef={contactRef}
          initialFile={pendingDropFile}
          onClose={() => { setMediaPickerOpen(false); setPendingDropFile(null); }}
          onSent={() => { setMediaPickerOpen(false); setPendingDropFile(null); load(); onSent?.(); }}
        />
      )}

      {mentionOpen && (
        <MentionDialog wsId={wsId} contactRef={contactRef}
                            members={members}
                            onClose={() => setMentionOpen(false)}
                            onDone={() => { setMentionOpen(false); load(); }} />
      )}
      {ccPrompt && (
        <CountryCodePromptDialog prompt={ccPrompt} onClose={() => setCcPrompt(null)} />
      )}
      {convertLeadOpen && (
        <ConvertToLeadDialog wsId={wsId}
                                     contactRef={contactRef}
                                     initialPhone={data.contact.phone || ""}
                                     initialName={data.contact.name || ""}
                                     initialEmail={data.contact.email || ""}
                                     onClose={() => setConvertLeadOpen(false)}
                                     onDone={(res) => {
                                       setConvertLeadOpen(false);
                                       // The thread's contact_ref changed on the backend — reload the
                                       // parent list so the row now shows the lead badge, and steer to
                                       // the new ref.
                                       onSent?.();
                                       if (res?.new_contact_ref) {
                                         // Nudge active pane; parent will pick up the fresh list.
                                         try { window.location.hash = ""; } catch { /* noop */ }
                                       }
                                     }} />
      )}
    </div>
  );
}


// R138t · Feb 2026 — Convert an unlinked WA thread to a CRM Lead. Small
// pre-filled form: name, phone (E.164), email (optional), company (optional).
function ConvertToLeadDialog({ wsId, contactRef, initialPhone, initialName,
                                              initialEmail, onClose, onDone }) {
  const [name, setName] = useState(initialName || "");
  const [phone, setPhone] = useState(() => {
    const raw = initialPhone || "";
    if (!raw) return "+91";
    const digits = raw.replace(/\D/g, "");
    if (raw.startsWith("+") && digits.length >= 11) return raw;
    // R138t · Auto-prefix +91 for bare 10-digit Indian numbers.
    return digits.length === 10 ? `+91${digits}` : (raw.startsWith("+") ? raw : `+${digits}`);
  });
  const [email, setEmail] = useState(initialEmail || "");
  const [company, setCompany] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 11) { toast.error("Phone must include the country code (e.g. +91)"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/workspaces/${wsId}/wa/threads/convert-to-lead`,
        { contact_ref: contactRef, name: name.trim(), phone,
            email: email.trim() || null, company: company.trim() || null });
      toast.success(data.lead_created
        ? `Lead created · ${data.messages_relinked} message${data.messages_relinked === 1 ? "" : "s"} relinked`
        : `Existing lead matched · ${data.messages_relinked} message${data.messages_relinked === 1 ? "" : "s"} relinked`);
      onDone?.(data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Convert failed"));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="wa-convert-lead-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-4 h-4 text-primary" /> Convert WhatsApp thread to Lead
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            This customer isn&apos;t linked to any CRM record yet. Create a Lead
            from this thread — we&apos;ll re-link every message to the new lead
            so replies land in the CRM funnel.
          </p>
          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Lead name *</label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
                       placeholder="e.g. Kishan Solanki"
                       data-testid="wa-convert-lead-name" />
          </div>
          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Phone (E.164) *</label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)}
                       placeholder="+919158889884"
                       className="font-mono"
                       data-testid="wa-convert-lead-phone" />
            <div className="text-[10px] text-muted-foreground">Must include country code. Indian numbers → +91 prefix.</div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Email</label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)}
                         placeholder="(optional)"
                         data-testid="wa-convert-lead-email" />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Company</label>
              <Input value={company} onChange={(e) => setCompany(e.target.value)}
                         placeholder="(optional)"
                         data-testid="wa-convert-lead-company" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="wa-convert-lead-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="wa-convert-lead-submit">
            {busy ? "Creating…" : "Create Lead & relink"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Iter Feb 2026 R21 · Ask HR/agents to fix a country-code-less phone
// number before it silently fails at Interakt. Two options: (a) jump to
// the CRM contact page to permanently correct the record; (b) apply a
// one-off E.164 override for the current send only.
function CountryCodePromptDialog({ prompt, onClose }) {
  const [override, setOverride] = useState(() => {
    const raw = prompt.prefill || "";
    if (!raw) return "+91";
    // Best-effort auto-prefix +91 for 10-digit India-style numbers
    const digits = raw.replace(/\D/g, "");
    return digits.length === 10 ? `+91${digits}` : (raw.startsWith("+") ? raw : `+${digits}`);
  });
  return (
    <div className="fixed inset-0 z-50 bg-background/80 flex items-center justify-center p-4"
          data-testid="wa-cc-prompt">
      <div className="bg-background border border-border p-5 w-full max-w-md space-y-3 shadow-lg">
        <div className="flex items-center gap-2 text-sm font-bold">
          <span className="text-amber-600">⚠</span> Missing country code
        </div>
        <p className="text-xs text-muted-foreground">{prompt.message}</p>
        <div className="space-y-1">
          <div className="label-eyebrow text-[10px]">One-off override (E.164)</div>
          <input value={override} onChange={(e) => setOverride(e.target.value)}
                  placeholder="+91XXXXXXXXXX"
                  className="w-full h-9 border border-border rounded-none px-2 text-sm"
                  data-testid="wa-cc-prompt-override" />
        </div>
        <div className="flex items-center justify-between gap-2 pt-2">
          {prompt.editUrl ? (
            <a href={prompt.editUrl} target="_blank" rel="noopener noreferrer"
                className="text-xs px-3 py-1.5 border border-border hover:bg-muted"
                data-testid="wa-cc-prompt-edit">
              Edit CRM contact →
            </a>
          ) : <span />}
          <div className="flex gap-2">
            <button type="button" onClick={onClose}
                     className="text-xs px-3 py-1.5 border border-border hover:bg-muted"
                     data-testid="wa-cc-prompt-cancel">Cancel</button>
            <button type="button"
                     disabled={!override.startsWith("+") || override.replace(/\D/g, "").length < 11}
                     onClick={() => prompt.retryFn?.(override)}
                     className="text-xs px-3 py-1.5 border border-primary bg-primary text-primary-foreground disabled:opacity-50"
                     data-testid="wa-cc-prompt-send">
              Send with override
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function MentionDialog({ wsId, contactRef, members, onClose, onDone }) {
  const [picked, setPicked] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const toggle = (uid) => setPicked((p) => p.includes(uid) ? p.filter(x => x !== uid) : [...p, uid]);
  const send = async () => {
    if (!picked.length) { toast.error("Pick at least one teammate"); return; }
    setBusy(true);
    try {
      const r = await api.post(
        `/workspaces/${wsId}/wa/threads/${encodeURIComponent(contactRef)}/mention`,
        { user_ids: picked, note: note.trim() || null });
      toast.success(`Notified ${r.data.notified} teammate(s)`);
      onDone();
    } catch (e) { toast.error(apiErrorMessage(e, "Mention failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="wa-mention-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><AtSign className="w-4 h-4 text-primary" />Mention teammates</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-foreground/70">
            Selected teammates get an in-app notification linking straight
            to this WhatsApp thread.
          </div>
          <div className="border border-border max-h-56 overflow-y-auto divide-y divide-border">
            {(members || []).length === 0 && (
              <div className="text-xs text-muted-foreground p-4 italic">No teammates found.</div>
            )}
            {(members || []).map((m) => {
              const uid = m.user_id;
              const nm = (m.user && (m.user.name || m.user.email)) || uid;
              const checked = picked.includes(uid);
              return (
                <label key={uid} className="flex items-center gap-2 px-2 py-1.5 text-xs cursor-pointer hover:bg-muted/30"
                          data-testid={`wa-mention-member-${uid}`}>
                  <input type="checkbox" checked={checked} onChange={() => toggle(uid)} />
                  <span className="flex-1 truncate">{nm}</span>
                  {m.user?.email && <span className="text-[10px] text-muted-foreground truncate">{m.user.email}</span>}
                </label>
              );
            })}
          </div>
          <div>
            <label className="label-eyebrow mb-1 inline-block">Note (optional)</label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                          placeholder="Any context you want to add?"
                          className="rounded-none text-xs" data-testid="wa-mention-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={send} disabled={busy || !picked.length}
                    data-testid="wa-mention-send">
            {busy ? "…" : `Notify ${picked.length || ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// Iter 253z-7 · Sync contacts from Interakt + expose the exact webhook URL
// admins must paste into Interakt so inbound replies land in this inbox.
function InteraktSyncPanel({ wsId, onSynced }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState(null);
  const [copied, setCopied] = useState(false);
  const loadInfo = async () => {
    try {
      const r = await api.get(`/workspaces/${wsId}/wa/webhook-url`);
      setInfo(r.data || null);
    } catch { setInfo(null); }
  };
  useEffect(() => { if (open) loadInfo(); /* eslint-disable-next-line */ }, [open]);
  const doSync = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/workspaces/${wsId}/wa/sync-interakt-contacts`);
      toast.success(`Synced ${r.data?.upserted || 0} contact${r.data?.upserted === 1 ? "" : "s"} from Interakt`);
      onSynced?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Sync failed")); }
    finally { setBusy(false); }
  };
  const copy = async () => {
    if (!info?.webhook_url) return;
    try {
      await navigator.clipboard.writeText(info.webhook_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Copy failed — select manually"); }
  };
  if (!open) {
    return (
      <Button variant="outline" size="sm" className="rounded-none h-8"
               onClick={() => setOpen(true)}
               data-testid="wa-inbox-interakt-sync-open">
        Interakt sync
      </Button>
    );
  }
  return (
    <div className="fixed inset-0 z-50 bg-background/80 flex items-start justify-center pt-20 p-4"
          onClick={() => setOpen(false)}
          data-testid="wa-inbox-interakt-sync-overlay">
      <div className="max-w-lg w-full border border-border bg-background shadow-lg p-4 space-y-3"
            onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          <MessageCircle className="w-4 h-4 text-primary" />
          <h3 className="font-bold text-sm">Interakt sync</h3>
          <button onClick={() => setOpen(false)} className="ml-auto text-xs text-muted-foreground hover:text-foreground">✕</button>
        </div>

        {/* Webhook Copy card */}
        <div className="border border-border p-3 bg-muted/30">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
            1. Paste this webhook into Interakt
          </div>
          <div className="text-[11px] text-muted-foreground mb-2">
            In Interakt → <em>Setup → Developer Setting → Webhooks</em> — add
            a subscription for <code className="font-mono bg-background px-1">Message Received</code>,{" "}
            <code className="font-mono bg-background px-1">Message Delivered</code> and{" "}
            <code className="font-mono bg-background px-1">Message Read</code> events pointing at:
          </div>
          {info?.webhook_url ? (
            <div className="flex items-center gap-2">
              <code className="flex-1 font-mono text-[10px] break-all bg-background border border-border px-2 py-1.5"
                      data-testid="wa-inbox-webhook-url">
                {info.webhook_url}
              </code>
              <Button size="sm" className="rounded-none h-8"
                       onClick={copy}
                       data-testid="wa-inbox-webhook-copy">
                {copied ? "Copied" : "Copy"}
              </Button>
            </div>
          ) : (
            <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 p-2">
              Webhook URL is not configured on this deployment — set the{" "}
              <code className="font-mono">APP_PUBLIC_URL</code> environment variable and
              redeploy, or contact Emergent Support.
            </div>
          )}
          <div className="text-[10px] text-muted-foreground mt-2">
            {info?.last_inbound_at
              ? <span className="text-emerald-700 font-semibold">✓ Last inbound received {new Date(info.last_inbound_at).toLocaleString()}</span>
              : <span>No inbound message received yet — once the webhook is live, replies appear here automatically.</span>}
          </div>
        </div>

        {/* Contact sync */}
        <div className="border border-border p-3 bg-muted/30">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
            2. Pull existing contacts
          </div>
          <div className="text-[11px] text-muted-foreground mb-2">
            Fetch your Interakt contact list into WhatsApp Inbox so you can
            start new outbound threads without waiting for a reply.
          </div>
          <Button size="sm" className="rounded-none h-8"
                   onClick={doSync} disabled={busy}
                   data-testid="wa-inbox-interakt-sync-run">
            {busy ? "Syncing…" : "Sync contacts from Interakt"}
          </Button>
        </div>

        {/* R26 · Webhook diagnostics · shows the last N Interakt hits so
             admins can self-diagnose why replies aren't landing (wrong URL,
             signature rejection, unknown event type, malformed JSON, or
             text-extraction failure). No shell access required. */}
        <WebhookDiagnosticsPanel wsId={wsId} />
      </div>
    </div>
  );
}


function WebhookDiagnosticsPanel({ wsId }) {
  const [rows, setRows] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/wa-webhook-inbox`, {
        params: { ws_id: wsId, provider: "interakt-any", limit: 20 },
      });
      setRows(r.data?.rows || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (expanded && rows === null) load(); /* eslint-disable-next-line */ }, [expanded]);
  return (
    <div className="border border-border p-3 bg-muted/30">
      <div className="flex items-center gap-2 mb-1">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
          3. Webhook diagnostics
        </div>
        {expanded && (
          <button onClick={load} disabled={loading}
                    className="ml-auto text-[10px] text-primary hover:underline"
                    data-testid="wa-inbox-webhook-diag-refresh">
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        )}
      </div>
      <div className="text-[11px] text-muted-foreground mb-2">
        Shows the last 20 payloads Interakt sent to this workspace. If this
        list is empty <em>and</em> customers claim to be replying, Interakt
        isn&apos;t reaching us — verify the webhook URL in Interakt console.
      </div>
      {!expanded ? (
        <Button size="sm" variant="outline" className="rounded-none h-8"
                 onClick={() => setExpanded(true)}
                 data-testid="wa-inbox-webhook-diag-open">
          Show recent webhook hits
        </Button>
      ) : (
        <div className="space-y-2 max-h-72 overflow-y-auto"
              data-testid="wa-inbox-webhook-diag-list">
          {rows === null || loading ? (
            <div className="text-[11px] text-muted-foreground">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 p-2 rounded"
                  data-testid="wa-inbox-webhook-diag-empty">
              No webhook payloads received yet. If Dipti is replying but you
              don&apos;t see her messages, Interakt likely isn&apos;t hitting our
              endpoint. Double-check the webhook URL in Interakt console.
            </div>
          ) : rows.map((r, i) => {
            const note = r.headers?._processing_note || "";
            const p = r.payload || {};
            const isProcessed = note === "processed";
            const isReceived = note === "received";
            const bg = isProcessed
              ? "bg-emerald-50 border-emerald-200"
              : note.startsWith("ignored") || note === "invalid_json" || note === "signature_invalid"
              ? "bg-red-50 border-red-200"
              : "bg-background border-border";
            const phone = p.parsed_phone || p._ignored_event_type || "";
            const text = p.parsed_text_head || p._parse_error || p._ignored_event_type || "";
            return (
              <div key={i} className={`text-[10px] font-mono border p-2 ${bg}`}
                    data-testid={`wa-inbox-webhook-diag-row-${i}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="uppercase font-bold text-[9px] tracking-wider">{note || "unknown"}</span>
                  <span className="ml-auto text-muted-foreground">
                    {new Date(r.received_at).toLocaleString()}
                  </span>
                </div>
                {isProcessed ? (
                  <div>
                    <div>phone: <span className="font-semibold">{phone || "—"}</span></div>
                    <div>text: {text ? <span className="font-semibold">{text}</span> : <em className="text-muted-foreground">(empty)</em>}</div>
                    <div>wa_message_id: {p.wa_message_id || <em className="text-muted-foreground">(none)</em>}</div>
                    <div>linked: {p.contact_source ? `${p.contact_source}:${p.contact_source_id}` : <em className="text-muted-foreground">(unlinked)</em>}</div>
                  </div>
                ) : isReceived ? (
                  <details>
                    <summary className="cursor-pointer text-muted-foreground">raw payload</summary>
                    <pre className="whitespace-pre-wrap break-all text-[9px] mt-1">{JSON.stringify(p, null, 2)}</pre>
                  </details>
                ) : (
                  <div>
                    <div>{text || <em className="text-muted-foreground">(no details)</em>}</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}



// Iter 253z-8 · Bulk template blast to all/selected Interakt-synced
// contacts. Filter by name/phone, pick a template, hit send.
function BulkWaSendPanel({ wsId, onSent }) {
  const [open, setOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [tplName, setTplName] = useState("");
  const [bodyVars, setBodyVars] = useState("");
  const [campaignLabel, setCampaignLabel] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => {
    if (!open) return;
    api.get(`/workspaces/${wsId}/wa/threads?limit=500`)
       .then((r) => setContacts((r.data?.rows || []).filter((x) => x.phone)))
       .catch(() => setContacts([]));
    api.get(`/workspaces/${wsId}/integrations/interakt/templates`)
       .then((r) => setTemplates(r.data?.rows || []))
       .catch(() => setTemplates([]));
  }, [open, wsId]);
  const filtered = contacts.filter((c) => {
    const needle = q.trim().toLowerCase();
    if (!needle) return true;
    return (c.name || "").toLowerCase().includes(needle) ||
              (c.phone || "").includes(needle) ||
              (c.email || "").toLowerCase().includes(needle);
  });
  const toggle = (phone) => {
    const next = new Set(selected);
    if (next.has(phone)) next.delete(phone); else next.add(phone);
    setSelected(next);
  };
  const selectAll = () => setSelected(new Set(filtered.map((c) => c.phone)));
  const clearSel = () => setSelected(new Set());
  const send = async () => {
    if (!tplName) { toast.error("Pick a template"); return; }
    if (!selected.size) { toast.error("Select at least one contact"); return; }
    setBusy(true);
    setResult(null);
    try {
      // Kick off the async job.
      const r = await api.post(`/workspaces/${wsId}/wa/bulk-send`, {
        phones: Array.from(selected),
        template_name: tplName,
        body_vars: bodyVars.split(",").map((s) => s.trim()).filter(Boolean),
        campaign_label: campaignLabel || null,
      });
      const jobId = r.data?.job_id;
      const total = r.data?.total || selected.size;
      setResult({ processed: 0, total, sent: 0, failed: 0, latest_results: [] });
      // SSE stream — pass auth via query since EventSource can't send headers.
      const backendBase = process.env.REACT_APP_BACKEND_URL || "";
      const token = localStorage.getItem("token") || "";
      const url = `${backendBase}/api/v1/workspaces/${wsId}/wa/bulk-send/${jobId}/stream?token=${encodeURIComponent(token)}`;
      // Fallback: fetch-based reader (auth headers work). Simpler than EventSource.
      const resp = await fetch(url.replace(`?token=${encodeURIComponent(token)}`, ""), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok || !resp.body) throw new Error("stream open failed");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      /* eslint-disable no-await-in-loop */
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const frames = buf.split("\n\n");
        buf = frames.pop() || "";
        for (const f of frames) {
          const line = f.split("\n").find((l) => l.startsWith("data:"));
          if (!line) continue;
          try {
            const frame = JSON.parse(line.slice(5).trim());
            if (frame.state) setResult(frame);
          } catch { /* ignore malformed */ }
        }
      }
      /* eslint-enable no-await-in-loop */
      toast.success("Bulk send complete");
      onSent?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Bulk send failed")); }
    finally { setBusy(false); }
  };
  if (!open) {
    return (
      <Button variant="outline" size="sm" className="rounded-none h-8"
               onClick={() => setOpen(true)}
               data-testid="wa-inbox-bulk-send-open">
        Bulk send
      </Button>
    );
  }
  return (
    <div className="fixed inset-0 z-50 bg-background/80 flex items-start justify-center pt-16 p-4"
          onClick={() => setOpen(false)}>
      <div className="max-w-2xl w-full border border-border bg-background shadow-lg p-4 space-y-3 max-h-[85vh] overflow-y-auto"
            data-testid="wa-inbox-bulk-send-dialog"
            onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          <MessageCircle className="w-4 h-4 text-primary" />
          <h3 className="font-bold text-sm">Bulk WhatsApp send</h3>
          <button onClick={() => setOpen(false)} className="ml-auto text-xs">✕</button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Template</label>
            <select value={tplName} onChange={(e) => setTplName(e.target.value)}
                    className="w-full h-9 border border-border px-2 text-sm bg-background rounded-none"
                    data-testid="wa-bulk-template">
              <option value="">— Pick a template —</option>
              {templates.map((t) => {
                const n = t.name || t.template_name;
                return <option key={n} value={n}>{n}{t.language ? ` · ${t.language}` : ""}</option>;
              })}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Body vars (comma-sep)</label>
            <Input value={bodyVars} onChange={(e) => setBodyVars(e.target.value)}
                    placeholder="e.g. Alice, 20 Feb"
                    className="h-9 rounded-none" data-testid="wa-bulk-vars" />
          </div>
        </div>
        <Input value={campaignLabel} onChange={(e) => setCampaignLabel(e.target.value)}
                placeholder="Campaign label (optional, for reporting)"
                className="h-9 rounded-none" data-testid="wa-bulk-label" />
        <div className="flex items-center gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Filter by name / phone…"
                  className="h-8 rounded-none flex-1" data-testid="wa-bulk-search" />
          <button onClick={selectAll} className="text-[10px] uppercase tracking-widest border border-border h-8 px-2 hover:bg-muted"
                  data-testid="wa-bulk-select-all">
            Select {filtered.length}
          </button>
          {selected.size > 0 && (
            <button onClick={clearSel} className="text-[10px] uppercase tracking-widest border border-border h-8 px-2 hover:bg-muted"
                    data-testid="wa-bulk-clear-sel">
              Clear {selected.size}
            </button>
          )}
        </div>
        <div className="border border-border max-h-56 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="p-4 text-center text-xs text-muted-foreground">No contacts — sync from Interakt first.</div>
          ) : filtered.map((c) => (
            <label key={c.phone} className="flex items-center gap-2 px-2 py-1 border-b border-border text-xs cursor-pointer hover:bg-muted/40"
                    data-testid={`wa-bulk-row-${c.phone.replace(/[^0-9]/g, "")}`}>
              <input type="checkbox" checked={selected.has(c.phone)}
                       onChange={() => toggle(c.phone)} />
              <span className="font-semibold flex-1">{c.name || c.phone}</span>
              <span className="text-muted-foreground font-mono text-[10px]">{c.phone}</span>
            </label>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-muted-foreground">
            Selected: <b>{selected.size}</b> / {filtered.length}
          </span>
          <Button className="rounded-none ml-auto" onClick={send} disabled={busy || !selected.size || !tplName}
                   data-testid="wa-bulk-send-submit">
            {busy ? "Sending…" : `Send to ${selected.size}`}
          </Button>
        </div>
        {result && (
          <div className="border border-border p-2 bg-muted/30 text-[11px] space-y-2"
                data-testid="wa-bulk-progress">
            <div className="flex items-center gap-2">
              <span>{result.processed}/{result.total} processed</span>
              <span className="text-emerald-700 font-semibold">✓ {result.sent}</span>
              <span className="text-rose-700 font-semibold">✗ {result.failed}</span>
              <span className="ml-auto text-[10px] uppercase tracking-widest text-muted-foreground">{result.state || "running"}</span>
            </div>
            {/* Progress bar */}
            <div className="h-1.5 bg-muted overflow-hidden">
              <div className="h-full bg-primary transition-all"
                    style={{ width: `${result.total ? (100 * result.processed / result.total) : 0}%` }}
                    data-testid="wa-bulk-progress-bar" />
            </div>
            {(result.latest_results || []).length > 0 && (
              <div className="max-h-24 overflow-y-auto border border-border">
                {result.latest_results.map((r, i) => (
                  <div key={i} className={`px-2 py-0.5 flex items-center gap-2 text-[10px] font-mono ${r.status === "sent" ? "text-emerald-700" : r.status === "failed" ? "text-rose-700" : "text-muted-foreground"}`}>
                    <span className="w-4">{r.status === "sent" ? "✓" : r.status === "failed" ? "✗" : "·"}</span>
                    <span className="flex-1 truncate">{r.phone}</span>
                    {r.error && <span className="text-rose-600 truncate max-w-[180px]">{r.error}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


// Iter 253z-8 · Design + submit a new Interakt template from inside
// Acquilix (POSTs to /v1/organizations/templates/ via our backend).
function InteraktTemplateEditorPanel({ wsId }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [language, setLanguage] = useState("en");
  const [category, setCategory] = useState("MARKETING");
  const [bodyText, setBodyText] = useState("");
  const [headerText, setHeaderText] = useState("");
  const [footerText, setFooterText] = useState("");
  const [examples, setExamples] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const submit = async () => {
    if (!name.trim() || !bodyText.trim()) {
      toast.error("Name + body text required"); return;
    }
    if (!/^[a-z0-9_]+$/.test(name)) {
      toast.error("Name must be lowercase letters/numbers/underscore only"); return;
    }
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post(`/workspaces/${wsId}/integrations/interakt/templates`, {
        name, language, category,
        body_text: bodyText,
        body_examples: examples.split(",").map((s) => s.trim()).filter(Boolean),
        header_text: headerText || null,
        footer_text: footerText || null,
      });
      setResult(r.data);
      toast.success("Template submitted to Interakt for approval");
    } catch (e) { toast.error(apiErrorMessage(e, "Submit failed")); }
    finally { setBusy(false); }
  };
  if (!open) {
    return (
      <Button variant="outline" size="sm" className="rounded-none h-8"
               onClick={() => setOpen(true)}
               data-testid="wa-inbox-tpl-editor-open">
        Design template
      </Button>
    );
  }
  return (
    <div className="fixed inset-0 z-50 bg-background/80 flex items-start justify-center pt-16 p-4"
          onClick={() => setOpen(false)}>
      <div className="max-w-lg w-full border border-border bg-background shadow-lg p-4 space-y-2 max-h-[85vh] overflow-y-auto"
            data-testid="wa-inbox-tpl-editor-dialog"
            onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          <MessageCircle className="w-4 h-4 text-primary" />
          <h3 className="font-bold text-sm">Design a new WhatsApp template</h3>
          <button onClick={() => setOpen(false)} className="ml-auto text-xs">✕</button>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Submits to Interakt for Meta approval — approval usually takes 15 min to a few hours.
        </p>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Template name (unique)</label>
            <Input value={name} onChange={(e) => setName(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"))}
                    placeholder="e.g. order_confirmation"
                    className="h-9 rounded-none font-mono" data-testid="wa-tpl-name" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Language</label>
            <select value={language} onChange={(e) => setLanguage(e.target.value)}
                    className="w-full h-9 px-2 border border-border bg-background text-sm rounded-none">
              <option value="en">English (en)</option>
              <option value="en_US">English US (en_US)</option>
              <option value="hi">Hindi (hi)</option>
              <option value="es">Spanish (es)</option>
              <option value="ar">Arabic (ar)</option>
              <option value="fr">French (fr)</option>
            </select>
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Category</label>
          <select value={category} onChange={(e) => setCategory(e.target.value)}
                  className="w-full h-9 px-2 border border-border bg-background text-sm rounded-none"
                  data-testid="wa-tpl-category">
            <option value="MARKETING">Marketing</option>
            <option value="UTILITY">Utility (transactional)</option>
            <option value="AUTHENTICATION">Authentication (OTP)</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Header text (optional, ≤60ch)</label>
          <Input value={headerText} onChange={(e) => setHeaderText(e.target.value.slice(0, 60))}
                  className="h-9 rounded-none" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Body text (use {"{{1}}"}, {"{{2}}"} for variables)</label>
          <textarea value={bodyText}
                       onChange={(e) => setBodyText(e.target.value)}
                       rows={4}
                       className="w-full border border-border p-2 text-xs font-mono"
                       placeholder="Hi {{1}}, your order #{{2}} is confirmed."
                       data-testid="wa-tpl-body" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Body variable examples (comma-sep, one per placeholder)</label>
          <Input value={examples} onChange={(e) => setExamples(e.target.value)}
                  placeholder="Alice, ORD-12345"
                  className="h-9 rounded-none" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Footer text (optional, ≤60ch)</label>
          <Input value={footerText} onChange={(e) => setFooterText(e.target.value.slice(0, 60))}
                  className="h-9 rounded-none" />
        </div>
        <div className="flex justify-end">
          <Button className="rounded-none" onClick={submit} disabled={busy}
                   data-testid="wa-tpl-submit">
            {busy ? "Submitting…" : "Submit for approval"}
          </Button>
        </div>
        {result && (
          <div className="border border-border p-2 bg-muted/30 text-[11px]">
            <b>Interakt response:</b>
            <pre className="whitespace-pre-wrap font-mono max-h-40 overflow-y-auto">{JSON.stringify(result.interakt_body, null, 2)}</pre>
          </div>
        )}
      </div>
    </div>
  );
}



// ═══════════════════════════════════════════════════════════════
// Iter Feb 2026 R24 · WA media components
// ═══════════════════════════════════════════════════════════════

const _kindIcon = {
  image: ImageIcon, video: Film, audio: Music, document: FileText,
};

// R131 · Lazy template body preview for legacy outbound rows where
// `text` is still `[template:xxx]`. Shared cache keyed by
// `${wsId}:${name}:${vars.join('|')}` so we don't refetch per bubble.
const _templateBodyCache = new Map();

function TemplateBodyPreview({ wsId, name, vars, fallback }) {
  const key = `${wsId}:${name}:${(vars || []).join("|")}`;
  const [body, setBody] = useState(() => _templateBodyCache.get(key) || null);
  useEffect(() => {
    if (_templateBodyCache.has(key)) { setBody(_templateBodyCache.get(key)); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.post(`/workspaces/${wsId}/wa/template-preview`,
                                     { template_name: name, body_vars: vars || [] });
        if (cancelled) return;
        let out;
        if (r.data.found) {
          out = (r.data.header ? `*${r.data.header}*\n\n` : "") + (r.data.body || "") +
                (r.data.footer ? `\n\n_${r.data.footer}_` : "");
        } else { out = fallback; }
        _templateBodyCache.set(key, out);
        setBody(out);
      } catch { setBody(fallback); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  if (!body) {
    return <span className="italic text-muted-foreground">{fallback} · loading body…</span>;
  }
  return body;
}


function MediaBubble({ mediaId, kind, contentType, filename }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [err, setErr] = useState(false);
  const Icon = _kindIcon[kind] || FileText;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/wa/media/${mediaId}/download`,
                                     { responseType: "blob" });
        if (cancelled) return;
        setBlobUrl(URL.createObjectURL(r.data));
      } catch (e) {
        if (!cancelled) setErr(true);
      }
    })();
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaId]);

  if (err) return (
    <div className="flex items-center gap-1.5 text-[10px] text-rose-600 italic mb-1">
      <ShieldAlert className="w-3 h-3" /> Media unavailable
    </div>
  );
  if (!blobUrl) return (
    <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground mb-1">
      <Loader2 className="w-3 h-3 animate-spin" /> Loading {kind || "media"}…
    </div>
  );
  if (kind === "image") return (
    <a href={blobUrl} target="_blank" rel="noopener noreferrer" className="block mb-1">
      <img src={blobUrl} alt={filename || "image"}
             className="max-w-full max-h-64 border border-border rounded-none"
             data-testid={`wa-msg-media-${mediaId}`} />
    </a>
  );
  if (kind === "video") return (
    <video controls src={blobUrl}
             className="max-w-full max-h-64 border border-border mb-1"
             data-testid={`wa-msg-media-${mediaId}`} />
  );
  if (kind === "audio") return (
    <audio controls src={blobUrl} className="mb-1 w-full"
             data-testid={`wa-msg-media-${mediaId}`} />
  );
  // document / fallback
  return (
    <a href={blobUrl} download={filename || "file"}
        className="flex items-center gap-1.5 border border-border p-1.5 hover:bg-muted/40 mb-1"
        data-testid={`wa-msg-media-${mediaId}`}>
      <Icon className="w-4 h-4 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="truncate text-[11px] font-medium">{filename || "attachment"}</div>
        <div className="text-[9px] text-muted-foreground">{contentType || "download"}</div>
      </div>
    </a>
  );
}


function MediaPickerDialog({ wsId, contactRef, onClose, onSent, initialFile = null }) {
  // R110 · If a file was drag-dropped onto the composer, start on the
  // Upload tab with the file already selected. User just adds a caption
  // (optional) and clicks Send — one-second flow.
  const [tab, setTab] = useState("upload"); // upload | library
  const [library, setLibrary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(initialFile);
  const [caption, setCaption] = useState("");
  const [pinToLibrary, setPinToLibrary] = useState(false);
  const [busy, setBusy] = useState(false);
  // R130 · Feb 2026 — Send-As Preview. After a successful upload, hold
  // the resulting media doc (with `public_url`) so admins can see the
  // exact URL Interakt will fetch before hitting Send. Split flow:
  //   1. "Upload" → shows preview URL + "Send now"
  //   2. "Send now" → posts to /send-media
  // The "Upload & send" one-shot button is preserved for speed.
  const [uploaded, setUploaded] = useState(null);

  useEffect(() => {
    if (tab !== "library") return;
    setLoading(true);
    api.get(`/workspaces/${wsId}/wa/media`, { params: { in_library: true, limit: 200 } })
      .then((r) => setLibrary(r.data?.rows || []))
      .catch(() => setLibrary([]))
      .finally(() => setLoading(false));
  }, [tab, wsId]);

  const sendMedia = async (mediaId, extraCaption) => {
    setBusy(true);
    try {
      // R109 · Feb 2026 — surface provider failures explicitly. Before
      // this the frontend fired `toast.success("Media sent")` even when
      // Interakt returned 400 (payload mismatch), so a user typing on
      // the paperclip flow got a green toast and no delivery — the
      // "silent failure" reported by production. Now we read the
      // provider block returned by the backend and toast accordingly.
      const r = await api.post(`/workspaces/${wsId}/wa/threads/${encodeURIComponent(contactRef)}/send-media`,
                        { media_id: mediaId, caption: extraCaption || caption || "" });
      const prov = r.data?.provider || {};
      if (prov.status === "sent") {
        toast.success("Media sent");
        onSent?.();
      } else if (prov.status === "skipped") {
        toast.error(prov.reason === "interakt_not_configured"
                        ? "Interakt is not configured for this workspace — connect it in Settings → Integrations."
                        : `Send skipped (${prov.reason || "unknown"})`);
      } else {
        const hint = prov.detail || prov.error || prov.reason || "Provider rejected the send.";
        toast.error(`Media send failed — ${hint}`, { duration: 6000 });
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, "Send failed"));
    } finally { setBusy(false); }
  };

  const uploadAndSend = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caption", caption || "");
      fd.append("pin_to_library", pinToLibrary ? "true" : "false");
      const r = await api.post(`/workspaces/${wsId}/wa/media/upload`, fd,
                                     { headers: { "Content-Type": "multipart/form-data" } });
      await sendMedia(r.data.id, caption);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Upload failed"));
      setBusy(false);
    }
  };

  // R130 · Feb 2026 — Two-step flow (upload → preview URL → send).
  const uploadOnly = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caption", caption || "");
      fd.append("pin_to_library", pinToLibrary ? "true" : "false");
      const r = await api.post(`/workspaces/${wsId}/wa/media/upload`, fd,
                                     { headers: { "Content-Type": "multipart/form-data" } });
      setUploaded(r.data);
      toast.success("Uploaded — review the URL and send when ready");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Upload failed"));
    } finally { setBusy(false); }
  };

  const sendUploaded = async () => {
    if (!uploaded) return;
    await sendMedia(uploaded.id, caption);
  };

  return (
    <div className="fixed inset-0 z-50 bg-background/80 flex items-center justify-center p-4"
          data-testid="wa-media-picker">
      <div className="bg-background border border-border p-4 w-full max-w-lg space-y-3 shadow-lg">
        <div className="flex items-center justify-between">
          <div className="text-sm font-bold">Attach media</div>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground"
                   data-testid="wa-media-picker-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex gap-1 border-b border-border">
          {["upload", "library"].map((t) => (
            <button key={t} onClick={() => setTab(t)}
                     data-testid={`wa-media-tab-${t}`}
                     className={`px-3 py-1.5 text-xs border-b-2 -mb-px ${tab === t ? "border-primary text-foreground font-medium" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
              {t === "upload" ? "Upload new" : "Library"}
            </button>
          ))}
        </div>

        {tab === "upload" && (
          <div className="space-y-2">
            <label className="block border border-dashed border-border p-4 text-center cursor-pointer hover:bg-muted/30"
                    data-testid="wa-media-file-drop">
              <Upload className="w-5 h-5 mx-auto mb-1 text-muted-foreground" />
              <div className="text-xs">
                {file ? file.name : "Click to pick a file (or drag & drop)"}
              </div>
              <div className="text-[9px] text-muted-foreground mt-1">
                JPEG · PNG · PDF · DOC · XLS · MP4 · MP3 · OGG · up to 100 MB (docs) / 16 MB (video/audio) / 5 MB (image)
              </div>
              <input type="file" className="hidden"
                      onChange={(e) => setFile(e.target.files?.[0] || null)}
                      data-testid="wa-media-file-input" />
            </label>
            <input type="text" placeholder="Optional caption"
                    value={caption} onChange={(e) => setCaption(e.target.value)}
                    className="w-full h-8 border border-border rounded-none px-2 text-xs"
                    data-testid="wa-media-caption" />
            <label className="flex items-center gap-1.5 text-[10px] text-muted-foreground cursor-pointer">
              <input type="checkbox" checked={pinToLibrary}
                      onChange={(e) => setPinToLibrary(e.target.checked)}
                      data-testid="wa-media-pin" />
                Pin to workspace media library (reusable across conversations)
            </label>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" size="sm" onClick={onClose} disabled={busy}
                       data-testid="wa-media-cancel">Cancel</Button>
              <Button size="sm" onClick={uploadAndSend} disabled={!file || busy}
                       data-testid="wa-media-upload-send">
                {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />}
                Upload & send
              </Button>
            </div>
          </div>
        )}

        {tab === "library" && (
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {loading && <div className="text-xs text-muted-foreground text-center p-4">Loading…</div>}
            {!loading && library.length === 0 && (
              <div className="text-xs text-muted-foreground text-center p-4 italic">
                No media pinned to the library yet. Upload a file and tick &quot;Pin to library&quot; to save it for reuse.
              </div>
            )}
            {library.map((m) => {
              const Icon = _kindIcon[m.media_kind] || FileText;
              return (
                <div key={m.id}
                      className="flex items-center gap-2 border border-border p-2 hover:bg-muted/30"
                      data-testid={`wa-media-lib-${m.id}`}>
                  <Icon className="w-4 h-4 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="truncate text-xs font-medium">{m.original_filename}</div>
                    <div className="text-[9px] text-muted-foreground">
                      {m.content_type} · {Math.round((m.size || 0) / 1024)} KB
                      {m.caption ? ` · "${m.caption}"` : ""}
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => sendMedia(m.id)} disabled={busy}
                           data-testid={`wa-media-lib-send-${m.id}`}>
                    <Send className="w-3 h-3 mr-1" /> Send
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// R132 · Feb 2026 — Admin one-click to walk every legacy outbound
// row whose `text` is still `[template:xxx]` and persist the rendered
// body so future bubbles skip the lazy fetch. Progress polls every 2s.
function BackfillTemplateBodiesPanel({ wsId, onDone }) {
  const [open, setOpen] = useState(false);
  const [candidates, setCandidates] = useState(null);
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);

  const loadCount = useCallback(async () => {
    try {
      const { data } = await api.get(
        `/workspaces/${wsId}/wa/backfill-template-bodies/candidates`);
      setCandidates(data.total);
    } catch { setCandidates(null); }
  }, [wsId]);

  useEffect(() => { if (open) loadCount(); }, [open, loadCount]);

  useEffect(() => {
    if (!job || job.status === "done" || job.status === "failed") {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(
          `/workspaces/${wsId}/wa/backfill-template-bodies/job/${job.id}`);
        setJob(data);
        if (data.status === "done") {
          toast.success(`Backfilled ${data.done} template rows` +
            (data.unresolved ? ` · ${data.unresolved} still unresolved (template not in cache)` : ""));
          loadCount();
          onDone?.();
        }
      } catch { /* keep polling */ }
    }, 2000);
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [job?.id, job?.status, wsId, loadCount, onDone]);

  const start = async () => {
    setStarting(true);
    try {
      const { data } = await api.post(
        `/workspaces/${wsId}/wa/backfill-template-bodies`,
        { batch_size: 50 });
      setJob({ id: data.job_id, status: "starting", total: 0, done: 0, unresolved: 0 });
      toast.success(data.resumed ? "Resumed running backfill" : "Backfill started");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Backfill failed to start"));
    } finally { setStarting(false); }
  };

  return (
    <>
      <Button size="sm" variant="outline" className="rounded-none h-8"
              onClick={() => setOpen(true)}
              data-testid="wa-backfill-template-bodies-open">
        <Wrench className="w-3.5 h-3.5 mr-1" />
        Backfill template bodies
      </Button>
      {open && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-lg rounded-none"
                          data-testid="wa-backfill-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Wrench className="w-4 h-4 text-amber-600" />
                Backfill legacy template bodies
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-xs">
              <p className="text-muted-foreground">
                Old outbound rows saved <code className="bg-muted px-1">[template:xxx]</code> instead
                of the full rendered body because the template wasn&apos;t cached at send-time. This
                walks every such row, resolves the body from the current template library, and
                stores it — future bubbles render instantly without lazy fetching.
              </p>
              <div className="border border-border p-2 flex items-center gap-2">
                <span className="text-muted-foreground">Rows to repair:</span>
                <span className="font-mono font-bold" data-testid="wa-backfill-candidates">
                  {candidates ?? "…"}
                </span>
                <Button size="sm" variant="ghost" className="ml-auto h-6 text-[10px]"
                        onClick={loadCount}
                        data-testid="wa-backfill-refresh">
                  <RefreshCw className="w-3 h-3" />
                </Button>
              </div>
              {job && (
                <div className="border border-amber-300 bg-amber-50 p-2 space-y-1"
                     data-testid="wa-backfill-progress">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">Job · {job.status}</span>
                    <span className="ml-auto font-mono tabular-nums text-amber-800">
                      {job.done || 0} / {job.total || 0} repaired
                      {job.unresolved ? ` · ${job.unresolved} unresolved` : ""}
                    </span>
                  </div>
                  <div className="h-2 bg-amber-200">
                    <div className={`h-2 ${job.status === "done" ? "bg-emerald-500" : "bg-amber-500"} transition-all`}
                         style={{ width: job.total > 0 ? `${Math.min(100, Math.round((job.done / job.total) * 100))}%` : "0%" }} />
                  </div>
                  {job.errors_head?.length > 0 && (
                    <details className="text-[10px] text-amber-900">
                      <summary>{job.errors_head.length} error(s)</summary>
                      <ul className="ml-4 mt-1 list-disc">
                        {job.errors_head.map((e, i) => (
                          <li key={i}><code>{e.id?.slice(0, 8)}…</code>: {e.error}</li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => setOpen(false)}
                        data-testid="wa-backfill-close">Close</Button>
                <Button size="sm" onClick={start}
                        disabled={starting || (job && job.status === "running") || candidates === 0}
                        data-testid="wa-backfill-start">
                  {starting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Wrench className="w-3 h-3 mr-1" />}
                  {job && job.status === "running" ? "Running…" : "Start backfill"}
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground">
                Templates not in your current library will be flagged as unresolved and skipped.
                Sync Interakt templates first if you expect a low unresolved count.
              </p>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

